import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Edit, Trash2, Mail, MessageSquare, Phone, Plus, Building2, MapPin, User, Loader2, History, MailCheck, Linkedin, Ban, Send, PhoneCall, FileText, Paperclip, X, Voicemail, CalendarPlus, Check, ChevronsUpDown, Search, Cpu, Copy, XCircle, FolderOpen, Upload, Image, File, Mic, RefreshCw, Clock, Reply, Forward, Sparkles, Wand2, Zap, PackageCheck, UserPlus, UserCheck, Eye, MousePointerClick, Lock, ChevronLeft, ChevronRight, Briefcase, ExternalLink } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import StatusSelect from '../../components/StatusSelect';
import useLeadStatuses from '../../hooks/useLeadStatuses';
import DraggableDialog from '../../components/DraggableDialog';
import VariablesHelper, { replaceVariables } from '../../components/VariablesHelper';
import TelnyxWebRTCCall from '../../components/TelnyxWebRTCCall';
import AttachFromLibraryModal from '../../components/AttachFromLibraryModal';
import { LogCallDialog } from '../../components/LogCallDialog';
import { formatPhone } from '../../utils/formatPhone';
import EmailTrackingBadges from '../../components/EmailTrackingBadges';
import ReactMarkdown from 'react-markdown';
import ContactStrategyCard from '../../components/crm/ContactStrategyCard';
import { CompanyResearchTab } from '../../components/crm/CompanyResearchTab';
import { CompanyDecisionMakersTab } from '../../components/crm/CompanyDecisionMakersTab';

// Custom hooks
import { useCrmLeadData } from '../../hooks/useCrmLeadData';
import { useCrmLeadProfile } from '../../hooks/useCrmLeadProfile';
import { useCrmCommunications } from '../../hooks/useCrmCommunications';
import { useCrmTelnyx } from '../../hooks/useCrmTelnyx';
import { useCrmNotes, useCrmTasks } from '../../hooks/useCrmNotesTasks';
import SourceSelect from '../../components/SourceSelect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

// Constants
const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'assigned', label: 'Assigned', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700' },
];
const PREFIXES = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const GENDERS = [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }];
const LEAD_TYPES = [
  { value: 'staffing_company', label: 'Staffing Company', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'client', label: 'Client', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'investor', label: 'Investor', color: 'bg-amber-100 text-amber-700' },
  { value: 'vendor', label: 'Vendor', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'franchise', label: 'Franchise', color: 'bg-purple-100 text-purple-700' },
  { value: 'misc', label: 'Misc', color: 'bg-slate-100 text-slate-700' },
];

// Helper functions
const formatShorthand = (value) => {
  if (!value) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  if (num >= 1000000000) return (num / 1000000000).toFixed(num % 1000000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'B';
  if (num >= 1000000) return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'K';
  return num.toLocaleString();
};

/**
 * Clean email content by removing HTML artifacts and MSO styles
 */
const cleanEmailContent = (content) => {
  if (!content) return '';
  
  let text = content;
  
  // Remove inline style attributes that weren't fully stripped
  text = text.replace(/style\s*=\s*['"][^'"]*['"]/gi, '');
  
  // Remove MSO-specific patterns
  text = text.replace(/mso-[^;:\s]+[:\s][^;'"]+[;]?/gi, '');
  
  // Remove leftover HTML attributes
  text = text.replace(/class\s*=\s*['"][^'"]*['"]/gi, '');
  
  // Remove remaining HTML tags that slipped through
  text = text.replace(/<[^>]+>/g, '');
  
  // Clean up CSS property values that leaked (e.g., position:absolute;margin-left:0)
  text = text.replace(/\b(?:position|margin|padding|width|height|z-index|visibility|display|font-family|font-size|color|background|border)\s*:\s*[^;\s]+[;]?\s*/gi, '');
  
  // Remove CSS units and values patterns
  text = text.replace(/\b\d+(?:\.\d+)?(?:pt|px|em|rem|%)\b/gi, '');
  
  // Clean up excessive whitespace
  text = text.replace(/\s+/g, ' ').trim();
  
  // Remove lines that are just CSS garbage
  const lines = text.split('\n');
  const cleanLines = lines.filter(line => {
    const trimmed = line.trim();
    // Skip lines that are mostly CSS/style remnants
    if (trimmed.length < 3) return false;
    if (/^[;\s\-:,\.]+$/.test(trimmed)) return false;
    if (/^\d+(?:\.\d+)?$/.test(trimmed)) return false;
    return true;
  });
  
  return cleanLines.join('\n').trim();
};

const getStatusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status);
  if (s) return <Badge className={s.color}>{s.label}</Badge>;
  // Custom status — display as-is with a neutral style
  return <Badge className="bg-slate-100 text-slate-700">{status || 'New'}</Badge>;
};

// Template Combobox Component
const TemplateCombobox = ({ templates, value, onChange, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectedTemplate = templates.find(t => t.id === value);
  const filteredTemplates = templates.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between mt-1 font-normal" data-testid={testId}>
          {selectedTemplate ? selectedTemplate.name : placeholder || "Select template..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search templates..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>
            <CommandGroup>
              {filteredTemplates.map((template) => (
                <CommandItem key={template.id} value={template.name} onSelect={() => { onChange(template.id); setOpen(false); setSearch(''); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === template.id ? 'opacity-100' : 'opacity-0'}`} />
                  {template.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('notes');
  const DEFAULT_TAB_ORDER = ['notes', 'activity', 'tasks', 'history'];
  const [tabOrder, setTabOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('jtcrm_lead_tab_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all default tabs are present (in case new tabs were added)
        const merged = [...parsed.filter(t => DEFAULT_TAB_ORDER.includes(t))];
        DEFAULT_TAB_ORDER.forEach(t => { if (!merged.includes(t)) merged.push(t); });
        return merged;
      }
    } catch {}
    return DEFAULT_TAB_ORDER;
  });
  const [dragTabId, setDragTabId] = useState(null);
  const [dragOverTabId, setDragOverTabId] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [reportsToSearchOpen, setReportsToSearchOpen] = useState(false);
  const [reportsToSearch, setReportsToSearch] = useState('');
  
  // Prev/next lead navigation from leads list
  const leadNavIds = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('jtcrm_lead_nav_ids') || '[]'); } catch { return []; }
  }, [id]);
  const currentIndex = leadNavIds.indexOf(id);
  const prevLeadId = currentIndex > 0 ? leadNavIds[currentIndex - 1] : null;
  const nextLeadId = currentIndex >= 0 && currentIndex < leadNavIds.length - 1 ? leadNavIds[currentIndex + 1] : null;

  // Users list for owner assignment
  const [allUsers, setAllUsers] = useState([]);
  const [ownerSearchOpen, setOwnerSearchOpen] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');

  // Lists management
  const [leadLists, setLeadLists] = useState([]);
  const [allLists, setAllLists] = useState([]);
  const [addToListDialogOpen, setAddToListDialogOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [addingToList, setAddingToList] = useState(false);
  
  // Sequence management
  const [leadEnrollments, setLeadEnrollments] = useState([]);
  const [allSequences, setAllSequences] = useState([]);
  const [enrollInSequenceDialogOpen, setEnrollInSequenceDialogOpen] = useState(false);
  const [selectedSequenceId, setSelectedSequenceId] = useState('');
  const [enrollingInSequence, setEnrollingInSequence] = useState(false);
  
  // Sync Old Emails state
  const [syncEmailsDialogOpen, setSyncEmailsDialogOpen] = useState(false);
  const [syncTimeValue, setSyncTimeValue] = useState('30');
  const [syncTimeUnit, setSyncTimeUnit] = useState('days');
  const [syncingEmails, setSyncingEmails] = useState(false);
  
  // Email Reply/Forward state
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailActionType, setEmailActionType] = useState(null); // 'reply', 'forward', or null
  
  // AI Email state
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiPromptType, setAiPromptType] = useState(null); // 'email' or 'sms'
  const [aiPromptText, setAiPromptText] = useState('');

  // Add New Company from Edit Lead state
  const [editCompanySearchQuery, setEditCompanySearchQuery] = useState('');
  const [showEditCompanySuggestions, setShowEditCompanySuggestions] = useState(false);
  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
  const [savingCompany, setSavingCompany] = useState(false);

  // Add Lead from Same Company state
  const [addLeadDialogOpen, setAddLeadDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarForm, setCalendarForm] = useState({ subject: '', start: '', end: '', notes: '' });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    first_name: '',
    last_name: '',
    prefix: '',
    title: '',
    work_email: '',
    work_direct_phone: '',
    mobile_phone: '',
    linkedin_url: '',
    x_twitter_url: '',
    linkedin_url_x: ''
  });
  const [savingNewLead, setSavingNewLead] = useState(false);

  // Data hook
  const { lead, setLead, notes, tasks, communications, auditLogs, companies, templates, allLeads, loading, leadCompany, companyColleagues, addCompanyLocal, refetch } = useCrmLeadData(id);

  // Dynamic tab title — show lead name
  useEffect(() => {
    if (lead?.first_name) {
      const name = `${lead.first_name} ${lead.last_name || ''}`.trim();
      document.title = `${name} | JTCRM`;
    }
    return () => { document.title = 'JTCRM'; };
  }, [lead?.first_name, lead?.last_name]);

  // Profile hook
  const profile = useCrmLeadProfile(id, lead, companies, refetch);

  // Communications hook
  const comms = useCrmCommunications(id, lead, templates, refetch);

  // Telnyx hook
  const telnyx = useCrmTelnyx(id, lead, comms.setCallForm, comms.setCallDialogOpen);

  // Notes hook
  const notesHook = useCrmNotes(id, refetch);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [savingEditNote, setSavingEditNote] = useState(false);

  // Tasks hook
  const tasksHook = useCrmTasks(id, refetch);

  // Custom statuses hook
  const { allStatuses, addStatus } = useLeadStatuses();

  // Row-level access: can this user communicate/edit/delete this lead?
  const canCommunicate = lead?.can_communicate ?? true;
  const canDelete = lead?.can_delete ?? true;
  const [claiming, setClaiming] = useState(false);

  // Research state
  const [research, setResearch] = useState(null);
  const [researching, setResearching] = useState(false);
  const [showFullReport, setShowFullReport] = useState(false);

  // Reset research state and fetch on lead change
  useEffect(() => {
    if (!id) return;
    setResearch(null);
    setResearching(false);
    setResearchStartTime(null);
    setResearchElapsed(0);
    setResearchProgress({ step: '', detail: '' });
    setShowFullReport(false);
    axios.get(`${API}/leads/${id}/research`).then(res => {
      if (res.data) {
        setResearch(res.data);
        if (res.data.status === 'in_progress') {
          setResearching(true);
          const startedAt = res.data.researched_at ? new Date(res.data.researched_at).getTime() : Date.now();
          setResearchStartTime(startedAt);
          setResearchProgress({ step: res.data.progress_step || 'searching', detail: res.data.progress_detail || '' });
        }
      }
    }).catch(() => {});
  }, [id]);

  // Research progress tracking
  const [researchProgress, setResearchProgress] = useState({ step: '', detail: '' });
  const [researchStartTime, setResearchStartTime] = useState(null);
  const [researchElapsed, setResearchElapsed] = useState(0);

  // Poll while research is in progress
  useEffect(() => {
    if (!researching || !id) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/leads/${id}/research`);
        if (res.data && res.data.status !== 'in_progress') {
          setResearch(res.data);
          setResearching(false);
          setResearchStartTime(null);
          if (res.data.status === 'completed') {
            setActiveTab('research');
            toast.success('Research completed');
          } else if (res.data.status === 'failed') {
            toast.error('Research failed: ' + (res.data.error || 'Unknown error'));
          }
        } else if (res.data) {
          setResearchProgress({ step: res.data.progress_step || '', detail: res.data.progress_detail || '' });
        }
      } catch { /* ignore polling errors */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [researching, id]);

  // Elapsed time ticker
  useEffect(() => {
    if (!researching || !researchStartTime) return;
    const timer = setInterval(() => {
      setResearchElapsed(Math.floor((Date.now() - researchStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [researching, researchStartTime]);

  const [researchMsgIndex, setResearchMsgIndex] = useState(0);
  const RESEARCH_MESSAGES = [
    "🔍 Googling harder than your ex stalking your profile...",
    "🕵️ Putting on my detective hat...",
    "🌐 Crawling the web like Spider-Man on espresso...",
    "📡 Intercepting satellite signals... just kidding, using Google...",
    "🧠 Teaching AI to read LinkedIn so you don't have to...",
    "🏃 Running through the internet at lightning speed...",
    "🔬 Analyzing data like a caffeinated scientist...",
    "🎯 Hunting for intel like a digital bounty hunter...",
    "📚 Reading their entire internet history... legally...",
    "🤖 Beep boop... processing human information...",
    "🦅 Eagle-eyeing every corner of the web...",
    "🎭 Unmasking their professional persona...",
    "🧩 Piecing together the puzzle of their career...",
    "☕ Brewing insights while scraping pages...",
    "🚀 Launching research rockets into cyberspace...",
    "🎪 Juggling 17 browser tabs simultaneously...",
    "🏄 Surfing the web waves for golden nuggets...",
    "🔮 Consulting the digital crystal ball...",
    "🐙 Reaching into every corner with octopus arms...",
    "💡 Having a eureka moment... any second now...",
  ];

  useEffect(() => {
    if (!researching) return;
    const msgInterval = setInterval(() => {
      setResearchMsgIndex(prev => (prev + 1) % RESEARCH_MESSAGES.length);
    }, 3000);
    return () => clearInterval(msgInterval);
  }, [researching]);

  const handleResearch = async () => {
    setResearching(true);
    setResearchMsgIndex(0);
    setResearchStartTime(Date.now());
    setResearchElapsed(0);
    setResearchProgress({ step: 'starting', detail: 'Initializing research agent' });
    try {
      const res = await axios.post(`${API}/leads/${id}/research`);
      setResearch(res.data);
      toast.success('Research started — max 2 minutes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Research failed to start');
      setResearching(false);
    }
  };

  const handleCancelResearch = async () => {
    try {
      await axios.delete(`${API}/leads/${id}/research`);
      setResearching(false);
      setResearchStartTime(null);
      setResearch(prev => prev ? { ...prev, status: 'cancelled' } : null);
      toast.success('Research cancelled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel research');
    }
  };

  const [fetchingPhoto, setFetchingPhoto] = useState(false);
  const handleFetchPhoto = async () => {
    setFetchingPhoto(true);
    try {
      const res = await axios.post(`${API}/leads/${id}/photo`);
      if (res.data.photo_url) {
        setLead(prev => ({ ...prev, photo_url: res.data.photo_url }));
        if (res.data.linkedin_url && !lead.linkedin_url) {
          setLead(prev => ({ ...prev, linkedin_url: res.data.linkedin_url }));
        }
        toast.success('Profile photo updated');
      } else {
        toast.info('No profile photo found on LinkedIn');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch photo');
    } finally {
      setFetchingPhoto(false);
    }
  };

  const handleClaimLead = async () => {
    setClaiming(true);
    try {
      await axios.put(`${API}/leads/${id}/claim`);
      toast.success('Lead claimed — you are now the owner');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim lead');
    } finally {
      setClaiming(false);
    }
  };

  // Fetch users for owner dropdown
  useEffect(() => {
    axios.get(`${API}/users/active-basic`).then(res => {
      setAllUsers(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, []);

  // Fetch lists when component mounts
  useEffect(() => {
    const fetchLists = async () => {
      try {
        // Fetch all available lists
        const listsRes = await axios.get(`${API}/franchise/lists`);
        setAllLists(listsRes.data || []);
        
        // Fetch lists this lead belongs to
        if (lead?.list_ids && lead.list_ids.length > 0) {
          const leadListsData = listsRes.data.filter(list => lead.list_ids.includes(list.id));
          setLeadLists(leadListsData);
        } else {
          setLeadLists([]);
        }
      } catch (error) {
        console.error('Failed to fetch lists:', error);
      }
    };
    
    const fetchSequences = async () => {
      try {
        // Fetch all active sequences
        const seqRes = await axios.get(`${API}/sequences?status=active`);
        setAllSequences(seqRes.data || []);
        
        // Fetch enrollments for this lead
        if (lead?.id) {
          const enrollRes = await axios.get(`${API}/sequences/enrollments/lead/${lead.id}`);
          setLeadEnrollments(enrollRes.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch sequences:', error);
      }
    };
    
    if (lead) {
      fetchLists();
      fetchSequences();
    }
  }, [lead?.id]);

  const handleAddToList = async () => {
    if (!selectedListId) {
      toast.error('Please select a list');
      return;
    }

    setAddingToList(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/add-to-list/${selectedListId}`);
      const selectedList = allLists.find(l => l.id === selectedListId);
      if (selectedList) {
        setLeadLists([...leadLists, selectedList]);
      }
      toast.success('Added to list');
      setAddToListDialogOpen(false);
      setSelectedListId('');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to list');
    } finally {
      setAddingToList(false);
    }
  };

  const handleEnrollInSequence = async () => {
    if (!selectedSequenceId) {
      toast.error('Please select a sequence');
      return;
    }

    setEnrollingInSequence(true);
    try {
      await axios.post(`${API}/sequences/${selectedSequenceId}/enroll`, {
        lead_ids: [lead.id]
      });
      toast.success('Lead enrolled in sequence');
      setEnrollInSequenceDialogOpen(false);
      setSelectedSequenceId('');
      
      // Refresh enrollments
      const enrollRes = await axios.get(`${API}/sequences/enrollments/lead/${lead.id}`);
      setLeadEnrollments(enrollRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll in sequence');
    } finally {
      setEnrollingInSequence(false);
    }
  };

  const handleRemoveFromSequence = async (sequenceId, enrollmentId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (!window.confirm('Remove lead from this sequence?')) return;

    try {
      await axios.delete(`${API}/sequences/${sequenceId}/enrollments/${enrollmentId}`);
      toast.success('Removed from sequence');
      setLeadEnrollments(leadEnrollments.filter(e => e.id !== enrollmentId));
    } catch (error) {
      console.error('Remove from sequence error:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove from sequence');
    }
  };

  // Initialize form data when lead loads
  // Track this lead as recently viewed for Quick Access panel
  useEffect(() => {
    if (lead?.id) {
      try {
        const stored = JSON.parse(localStorage.getItem('recentLeads') || '[]');
        const entry = {
          id: lead.id,
          first_name: lead.first_name,
          last_name: lead.last_name,
          company_name: lead.company_name || '',
          title: lead.title || '',
          viewed_at: new Date().toISOString()
        };
        const filtered = stored.filter(l => l.id !== lead.id);
        filtered.unshift(entry);
        localStorage.setItem('recentLeads', JSON.stringify(filtered.slice(0, 20)));
      } catch (e) { /* ignore localStorage errors */ }
    }
  }, [lead?.id]);

  useEffect(() => {
    if (lead) {
      profile.initFormData(lead);
      comms.initForms(lead);

      // Auto-open dialogs based on navigation state
      if (location.state?.openEmail) {
        comms.openEmailDialog();
        // Clear state to prevent reopening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      } else if (location.state?.openCall) {
        comms.openLogCallDialog();
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [lead]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed values
  const pendingTasks = useMemo(() => tasks.filter(t => !t.completed), [tasks]);
  const completedTasks = useMemo(() => tasks.filter(t => t.completed), [tasks]);
  const emailTemplates = useMemo(() => templates.filter(t => t.type === 'email'), [templates]);
  const smsTemplates = useMemo(() => templates.filter(t => t.type === 'sms'), [templates]);

  // Filter leads for search
  const filteredSearchLeads = useMemo(() => {
    if (!leadSearchQuery.trim()) return [];
    const words = leadSearchQuery.toLowerCase().trim().split(/\s+/);
    return allLeads.filter(l => {
      if (l.id === id) return false;
      const textFields = [
        (l.first_name || '').toLowerCase(),
        (l.last_name || '').toLowerCase(),
        (l.company_name || '').toLowerCase(),
        (l.work_email || '').toLowerCase()
      ];
      const fullName = `${(l.first_name || '')} ${(l.last_name || '')}`.toLowerCase();
      return words.every(word =>
        textFields.some(field => field.includes(word)) || fullName.includes(word)
      );
    }).slice(0, 10);
  }, [allLeads, leadSearchQuery, id]);

  // Filtered communications
  const filteredCommunications = useMemo(() => {
    if (activityFilter === 'all') return communications;
    if (activityFilter === 'call_recording') return communications.filter(c => c.type === 'call_recording' || c.has_recording);
    return communications.filter(c => c.type === activityFilter);
  }, [communications, activityFilter]);

  // Toggle envelope mailed status
  const handleToggleEnvelopeMailed = async () => {
    try {
      const newStatus = !lead.envelope_mailed;
      await axios.put(`${API}/leads/${id}`, { envelope_mailed: newStatus });
      toast.success(newStatus ? 'Marked as mailed' : 'Marked as not mailed');
      refetch();
    } catch { toast.error('Failed to update envelope status'); }
  };

  // Sync Old Emails from Office 365
  const handleSyncOldEmails = async () => {
    if (!lead.work_email && !lead.personal_email) {
      toast.error('No email address found for this lead');
      return;
    }
    
    // Convert time to days
    let days = parseInt(syncTimeValue) || 30;
    if (syncTimeUnit === 'weeks') days *= 7;
    else if (syncTimeUnit === 'months') days *= 30;
    else if (syncTimeUnit === 'years') days *= 365;
    
    setSyncingEmails(true);
    try {
      const response = await axios.post(`${API}/leads/${id}/sync-emails`, {
        email: lead.work_email || lead.personal_email,
        days_back: days
      });
      
      if (response.data.synced_count > 0) {
        // Successfully synced emails
        const inboxMsg = response.data.inbox_count ? `${response.data.inbox_count} received` : '';
        const sentMsg = response.data.sent_count ? `${response.data.sent_count} sent` : '';
        const details = [inboxMsg, sentMsg].filter(Boolean).join(', ');
        toast.success(`Synced ${response.data.synced_count} email(s)${details ? ` (${details})` : ''}`);
        refetch(); // Refresh to show new emails
        setSyncEmailsDialogOpen(false);
      } else if (response.data.folders_to_search) {
        // Office 365 not connected
        toast.warning(
          <div>
            <strong>Office 365 not connected</strong>
            <p className="text-sm mt-1">Connect your Office 365 account in Settings to sync emails from your Inbox and Sent folders.</p>
          </div>,
          { duration: 6000 }
        );
      } else if (response.data.message?.includes('not connected')) {
        // Alternative check for not connected message
        toast.warning('Office 365 not connected. Please connect in Settings to sync your emails.', { duration: 5000 });
      } else {
        // Office 365 connected but no new emails found
        toast.info('No new emails found in the specified time period');
        setSyncEmailsDialogOpen(false);
      }
    } catch (error) {
      console.error('Sync emails error:', error);
      if (error.response?.status === 401 || error.response?.data?.detail?.includes('Office 365')) {
        toast.error('Office 365 not connected. Please connect in Settings first.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to sync emails');
      }
    } finally {
      setSyncingEmails(false);
    }
  };

  // Clean synced emails (remove HTML artifacts)
  const handleCleanEmails = async () => {
    try {
      const response = await axios.post(`${API}/leads/${id}/clean-emails`);
      if (response.data.cleaned_count > 0) {
        toast.success(`Cleaned ${response.data.cleaned_count} email(s)`);
        refetch(); // Refresh to show cleaned emails
      } else {
        toast.info('All emails are already clean');
      }
    } catch (error) {
      console.error('Clean emails error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clean emails');
    }
  };

  // Handle clicking on an email to reply or forward
  const handleEmailClick = (email, actionType = 'reply') => {
    setSelectedEmail(email);
    setEmailActionType(actionType);
    
    // Determine the "to" address
    let toAddress = '';
    if (actionType === 'reply') {
      // Reply goes to whoever sent the email (if inbound) or the original recipient (if outbound)
      if (email.direction === 'inbound' && email.from_email) {
        toAddress = email.from_email;
      } else {
        toAddress = email.to || lead?.work_email || lead?.personal_email || '';
      }
    }
    // For forward, leave "to" empty
    
    // Build the reply/forward body
    const originalDate = email.created_at ? format(parseISO(email.created_at), 'MMM d, yyyy h:mm a') : '';
    const fromLine = email.from_email || email.user_name || 'Unknown';
    const toLine = email.to || '';
    
    let quotedContent = `\n\n`;
    if (actionType === 'reply') {
      quotedContent += `---------- Original Message ----------\n`;
    } else {
      quotedContent += `---------- Forwarded Message ----------\n`;
    }
    quotedContent += `From: ${fromLine}\n`;
    quotedContent += `Date: ${originalDate}\n`;
    quotedContent += `To: ${toLine}\n`;
    quotedContent += `Subject: ${email.subject || 'No Subject'}\n\n`;
    quotedContent += email.content || '';
    
    // Build the subject
    let newSubject = email.subject || '';
    if (actionType === 'reply' && !newSubject.toLowerCase().startsWith('re:')) {
      newSubject = `Re: ${newSubject}`;
    } else if (actionType === 'forward' && !newSubject.toLowerCase().startsWith('fwd:')) {
      newSubject = `Fwd: ${newSubject}`;
    }
    
    // Set the form data
    comms.setEmailForm({
      to: toAddress,
      cc: '',
      bcc: '',
      subject: newSubject,
      body: quotedContent,
      template_id: '',
      attachments: []
    });
    
    comms.setEmailDialogOpen(true);
  };

  // Handle SMS reply click
  const handleSmsReplyClick = (sms) => {
    comms.setSmsForm({
      to: sms.to || sms.from_number || lead?.mobile_phone || '',
      message: '',
      template_id: ''
    });
    comms.setSmsDialogOpen(true);
  };

  // Handle call click from activity
  const handleCallClick = (comm) => {
    const phoneNumber = comm.to || comm.phone_number || lead?.work_direct_phone || lead?.mobile_phone || '';
    telnyx.handleClickToCall(phoneNumber);
  };

  // Handle log call click from activity
  const handleLogCallClick = (comm) => {
    const phoneNumber = comm.to || comm.phone_number || lead?.work_direct_phone || lead?.mobile_phone || '';
    comms.setCallForm(f => ({
      ...f,
      phone_number: phoneNumber,
      notes: '',
      call_outcome: 'connected',
      duration_seconds: 0
    }));
    comms.setCallDialogOpen(true);
  };

  // Open AI Prompt Dialog
  const openAiPromptDialog = (type) => {
    setAiPromptType(type);
    setAiPromptText('');
    setAiPromptDialogOpen(true);
  };

  // AI Write - Generate email content using AI
  const handleAiEmailGenerate = async (promptText) => {
    setAiProcessing(true);
    setAiPromptDialogOpen(false);
    try {
      // If user provided a topic, always compose a new email based on that topic
      // Only treat as reply if there's a selected email AND no custom topic
      const isReplyMode = !!selectedEmail?.content && !promptText?.trim();
      
      const response = await axios.post(`${API}/ai/email-assistant`, {
        original_email: isReplyMode ? selectedEmail?.content : '',
        action: isReplyMode ? 'reply' : 'compose',
        lead_name: lead?.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null,
        lead_company: lead?.company_name || null,
        subject: comms.emailForm.subject || '',
        topic: promptText || ''
      });
      
      // Get current body and find where to insert the AI content
      const currentBody = comms.emailForm.body;
      const separatorIndex = currentBody.indexOf('---------- Original Message ----------');
      const forwardSeparatorIndex = currentBody.indexOf('---------- Forwarded Message ----------');
      
      let newBody = response.data.result;
      if (separatorIndex > -1) {
        newBody = response.data.result + '\n' + currentBody.substring(separatorIndex);
      } else if (forwardSeparatorIndex > -1) {
        newBody = response.data.result + '\n' + currentBody.substring(forwardSeparatorIndex);
      }
      
      comms.setEmailForm(f => ({ ...f, body: newBody }));
      toast.success('AI email generated');
    } catch (error) {
      console.error('AI error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI content');
    } finally {
      setAiProcessing(false);
    }
  };

  // AI Rewrite - Rewrite user's draft to be more professional
  const handleAiRewrite = async () => {
    const currentBody = comms.emailForm.body;
    
    // Extract just the user's response (before the quoted content)
    let userDraft = currentBody;
    const separatorIndex = currentBody.indexOf('---------- Original Message ----------');
    const forwardSeparatorIndex = currentBody.indexOf('---------- Forwarded Message ----------');
    
    if (separatorIndex > -1) {
      userDraft = currentBody.substring(0, separatorIndex).trim();
    } else if (forwardSeparatorIndex > -1) {
      userDraft = currentBody.substring(0, forwardSeparatorIndex).trim();
    }
    
    if (!userDraft) {
      toast.error('Please write your response first before rewriting');
      return;
    }
    
    setAiProcessing(true);
    try {
      const response = await axios.post(`${API}/ai/email-assistant`, {
        original_email: selectedEmail?.content || '',
        user_draft: userDraft,
        action: 'rewrite',
        lead_name: lead?.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null,
        lead_company: lead?.company_name || null
      });
      
      // Replace user's draft with the rewritten version
      let newBody = response.data.result;
      if (separatorIndex > -1) {
        newBody = response.data.result + '\n\n' + currentBody.substring(separatorIndex);
      } else if (forwardSeparatorIndex > -1) {
        newBody = response.data.result + '\n\n' + currentBody.substring(forwardSeparatorIndex);
      }
      
      comms.setEmailForm(f => ({ ...f, body: newBody }));
      toast.success('Email rewritten by AI');
    } catch (error) {
      console.error('AI rewrite error:', error);
      toast.error(error.response?.data?.detail || 'Failed to rewrite with AI');
    } finally {
      setAiProcessing(false);
    }
  };

  // AI SMS Write - Generate SMS content using AI
  const handleAiSmsGenerate = async (promptText) => {
    setAiProcessing(true);
    setAiPromptDialogOpen(false);
    try {
      const response = await axios.post(`${API}/ai/sms-assistant`, {
        action: 'compose',
        lead_name: lead?.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null,
        lead_company: lead?.company_name || null,
        topic: promptText || ''
      });
      
      comms.setSmsForm(f => ({ ...f, message: response.data.result }));
      toast.success('AI SMS generated');
    } catch (error) {
      console.error('AI SMS error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AI SMS');
    } finally {
      setAiProcessing(false);
    }
  };

  // AI SMS Rewrite - Rewrite user's SMS draft to be more professional
  const handleAiSmsRewrite = async () => {
    const userDraft = comms.smsForm.message.trim();
    
    if (!userDraft) {
      toast.error('Please write your message first before rewriting');
      return;
    }
    
    setAiProcessing(true);
    try {
      const response = await axios.post(`${API}/ai/sms-assistant`, {
        action: 'rewrite',
        user_draft: userDraft,
        lead_name: lead?.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null,
        lead_company: lead?.company_name || null
      });
      
      comms.setSmsForm(f => ({ ...f, message: response.data.result }));
      toast.success('SMS rewritten by AI');
    } catch (error) {
      console.error('AI SMS rewrite error:', error);
      toast.error(error.response?.data?.detail || 'Failed to rewrite SMS');
    } finally {
      setAiProcessing(false);
    }
  };

  // Handle AI Prompt Submit
  const handleAiPromptSubmit = () => {
    if (aiPromptType === 'email') {
      handleAiEmailGenerate(aiPromptText);
    } else if (aiPromptType === 'sms') {
      handleAiSmsGenerate(aiPromptText);
    }
  };

  // Handle Add Lead from Same Company
  const handleAssignToMe = async () => {
    if (!currentUser) return;
    try {
      await axios.put(`${API}/leads/${id}`, { owner_id: currentUser.id, owner_name: currentUser.full_name });
      toast.success('Lead assigned to you');
      refetch();
    } catch {
      toast.error('Failed to assign lead');
    }
  };

  const handleCreateCompanyFromEdit = async () => {
    if (!newCompanyForm.name.trim()) { toast.error('Company name is required'); return; }
    setSavingCompany(true);
    try {
      const payload = { ...newCompanyForm };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      payload.name = payload.name.trim();
      const res = await axios.post(`${API}/companies`, payload);
      const newCompany = { id: res.data.id, name: res.data.name || newCompanyForm.name.trim(), locations: [] };
      // Add to local companies list + cache so it's immediately available
      addCompanyLocal(newCompany);
      // Update the profile form with the new company — set directly to avoid stale closure
      profile.setFormData(prev => ({ ...prev, company_id: newCompany.id, location_id: '' }));
      setEditCompanySearchQuery(newCompany.name);
      setNewCompanyDialogOpen(false);
      setNewCompanyForm({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
      toast.success(`Company "${newCompany.name}" created — click Save to update the lead`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleOpenAddLeadDialog = () => {
    setNewLeadForm({
      first_name: '',
      last_name: '',
      prefix: '',
      title: '',
      work_email: '',
      work_direct_phone: '',
      mobile_phone: '',
      linkedin_url: '',
      x_twitter_url: '',
      linkedin_url_x: '',
      company_id: lead.company_id || '',
      location_id: lead.location_id || ''
    });
    setAddLeadDialogOpen(true);
  };

  const handleCreateNewLead = async () => {
    if (!newLeadForm.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    
    setSavingNewLead(true);
    try {
      const payload = { ...newLeadForm };
      
      // Clean up empty fields
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === 'none') payload[k] = null;
      });
      
      // Ensure lead_types has at least Staffing Company if empty
      if (!payload.lead_types || payload.lead_types.length === 0) {
        payload.lead_types = ['staffing_company'];
      }
      
      const response = await axios.post(`${API}/leads`, payload);
      toast.success('New lead created from same company');
      setAddLeadDialogOpen(false);
      
      // Navigate to the new lead
      navigate(`/admin/leads/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSavingNewLead(false);
    }
  };


  const openEditDialog = () => {
    profile.setFormData(lead);
    if (lead.company_id) {
      const company = companies.find(c => c.id === lead.company_id);
      profile.setSelectedCompanyLocations?.(company?.locations || []);
      setEditCompanySearchQuery(company?.name || '');
    } else {
      setEditCompanySearchQuery('');
    }
    profile.setEditDialogOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!lead) return null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Lead Search Bar */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/leads')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {leadNavIds.length > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={!prevLeadId} onClick={() => prevLeadId && navigate(`/admin/leads/${prevLeadId}`)} title="Previous lead">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-slate-400 min-w-[3rem] text-center">{currentIndex + 1} / {leadNavIds.length}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={!nextLeadId} onClick={() => nextLeadId && navigate(`/admin/leads/${nextLeadId}`)} title="Next lead">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="flex-1 max-w-md relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search leads..."
              value={leadSearchQuery}
              onChange={(e) => { setLeadSearchQuery(e.target.value); if (e.target.value.trim()) setLeadSearchOpen(true); else setLeadSearchOpen(false); }}
              onFocus={() => leadSearchQuery.trim() && setLeadSearchOpen(true)}
              onBlur={() => setTimeout(() => setLeadSearchOpen(false), 200)}
              className="pl-9 h-10 bg-white"
              data-testid="lead-search-input"
            />
            {leadSearchQuery && (
              <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onMouseDown={(e) => e.preventDefault()} onClick={() => { setLeadSearchQuery(''); setLeadSearchOpen(false); }}>
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {leadSearchOpen && filteredSearchLeads.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-[300px] overflow-y-auto">
              {filteredSearchLeads.map((l) => (
                <button key={l.id} className="w-full px-3 py-2 text-left hover:bg-slate-100 flex items-center gap-3 border-b last:border-0" onMouseDown={(e) => e.preventDefault()} onClick={() => { setLeadSearchOpen(false); setLeadSearchQuery(''); navigate(`/admin/leads/${l.id}`); }}>
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-indigo-600" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{l.first_name} {l.last_name}</p>
                    <p className="text-xs text-slate-500 truncate">{l.company_name || l.work_email || 'No company'}</p>
                  </div>
                  {l.dnc && <Badge variant="outline" className="text-red-600 text-xs">DNC</Badge>}
                </button>
              ))}
            </div>
          )}
          {leadSearchOpen && leadSearchQuery.trim() && filteredSearchLeads.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4 text-center text-slate-500 text-sm">No leads found</div>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          {(lead.photo_url || research?.photo_url) ? (
            <img src={lead.photo_url || research.photo_url} alt={`${lead.first_name} ${lead.last_name}`} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md shrink-0" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }} />
          ) : null}
          {!(lead.photo_url || research?.photo_url) && (
            <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 text-indigo-600 font-bold text-lg">
              {(lead.first_name?.[0] || '')}{(lead.last_name?.[0] || '')}
            </div>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{lead.prefix && (lead.prefix + ' ')}{lead.first_name} {lead.last_name}</h1>
              {(lead.linkedin_url || research?.found_linkedin) && (
                <a href={lead.linkedin_url || research.found_linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-full hover:bg-blue-50 transition-colors text-[#0A66C2] hover:text-[#004182]" title="View LinkedIn Profile">
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
              <button onClick={openEditDialog} disabled={!canCommunicate} className="p-1.5 rounded-full hover:bg-slate-100 transition-colors text-slate-400 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none" data-testid="edit-lead-btn" title={canCommunicate ? "Edit Lead" : "Only the lead owner can edit"}>
                <Edit className="h-4 w-4" />
              </button>
              {getStatusBadge(lead.status)}
              {lead.owner_id && (
                <Badge variant="outline" className={`text-xs font-normal ${lead.owner_id === currentUser?.id ? 'text-indigo-600 border-indigo-300' : 'text-slate-600 border-slate-300'}`}>
                  <User className="h-3 w-3 mr-1" />
                  {lead.owner_id === currentUser?.id ? 'You' : (lead.owner_name || 'Assigned')}
                </Badge>
              )}
            </div>
            <p className="text-slate-600 mt-1">{lead.title || 'No title'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lead?.status === 'new' && !lead?.owner_id && (
            <Button variant="outline" size="sm" onClick={handleClaimLead} disabled={claiming} title="Become the owner of this lead">
              <UserCheck className="h-4 w-4 mr-1" /> {claiming ? 'Claiming...' : 'Claim Ownership'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleAssignToMe} disabled={!canCommunicate} title={!canCommunicate ? "Only the lead owner can reassign" : "Assign this lead to yourself"}>
            <User className="h-4 w-4 mr-1" /> Assign to Me
          </Button>
          <Button variant="outline" size="sm" onClick={handleOpenAddLeadDialog} disabled={!canCommunicate} className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300 disabled:opacity-40 disabled:cursor-not-allowed" title={!canCommunicate ? "Only the lead owner can add contacts" : "Add lead from company"}>
            <UserPlus className="h-4 w-4 mr-1" /> Add from Company
          </Button>
          <Button variant="outline" size="sm" onClick={comms.openEmailDialog} data-testid="email-btn" disabled={lead.dne || !canCommunicate} title={!canCommunicate ? "Only the lead owner can email" : lead.dne ? "Do Not Email is enabled" : "Send Email"}>
            <Mail className="h-4 w-4 mr-1" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={comms.openSmsDialog} data-testid="sms-btn" disabled={!canCommunicate} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"}><MessageSquare className="h-4 w-4 mr-1" /> SMS</Button>
          <Button variant="outline" size="sm" onClick={comms.openLogCallDialog} data-testid="call-btn" disabled={lead.dnc || !canCommunicate} title={!canCommunicate ? "Only the lead owner can call" : lead.dnc ? "Do Not Call is enabled" : "Log Call"}>
            <PhoneCall className="h-4 w-4 mr-1" /> Log Call
          </Button>
          <Button variant="outline" size="sm" onClick={() => tasksHook.setTaskDialogOpen(true)} data-testid="task-btn" disabled={!canCommunicate} title={!canCommunicate ? "Only the lead owner can add tasks" : "Add Task"}><Plus className="h-4 w-4 mr-1" /> Task</Button>
          <Button variant="outline" size="sm" onClick={() => {
            const now = new Date();
            now.setDate(now.getDate() + 1);
            while (now.getDay() === 0 || now.getDay() === 6) now.setDate(now.getDate() + 1);
            const startStr = `${now.toISOString().split('T')[0]}T10:00`;
            const endStr = `${now.toISOString().split('T')[0]}T10:30`;
            setCalendarForm({ subject: `Meeting: ${lead.first_name} ${lead.last_name}${lead.company_name ? ` - ${lead.company_name}` : ''}`, start: startStr, end: endStr, notes: '' });
            setCalendarDialogOpen(true);
          }} data-testid="calendar-btn" disabled={!canCommunicate} title={!canCommunicate ? "Only the lead owner can schedule meetings" : "Schedule Meeting"}><CalendarPlus className="h-4 w-4 mr-1" /> Calendar</Button>
          <Button variant="outline" size="sm" onClick={handleResearch} disabled={researching} className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300" title="Deep web research on this lead" data-testid="research-btn">
            {researching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} {researching ? 'Researching...' : 'Research'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleFetchPhoto} disabled={fetchingPhoto || researching} className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300" title="Fetch profile photo from LinkedIn via Apify">
            {fetchingPhoto ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Image className="h-4 w-4 mr-1" />} {fetchingPhoto ? 'Fetching...' : 'Get Pic'}
          </Button>
          {researching && (
            <Button variant="ghost" size="sm" onClick={handleCancelResearch} className="text-slate-400 hover:text-red-500 px-2 h-8" title="Cancel research">
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" className="text-red-600" onClick={() => profile.setDeleteDialogOpen(true)} disabled={!canDelete} title={canDelete ? undefined : "Only the lead owner can delete"} data-testid="delete-btn"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
        </div>
      </div>

      {!canCommunicate && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm flex items-center gap-2">
          <span className="font-medium">View only</span> — only Notes are accessible. All actions are available to the lead owner only.
          {lead?.status === 'new' && !lead?.owner_id && (
            <button onClick={handleClaimLead} disabled={claiming} className="ml-auto text-xs px-2 py-0.5 rounded bg-amber-200 hover:bg-amber-300 font-medium transition-colors">
              {claiming ? 'Claiming...' : 'Claim to get access'}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
        {/* Left Sidebar - Ultra Compact Design */}
        <div className="space-y-3">
          {/* Header Card - Ultra Compact */}
          <Card>
            <CardContent className="pt-3 pb-3 px-3">
              <div className="text-left">
                {/* Name and Action Icons - Same Row */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-bold text-slate-900 leading-tight">{lead.prefix && (lead.prefix + ' ')}{lead.first_name} {lead.last_name}</h2>
                  </div>
                  {/* Action Icons - Right Side */}
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <Button size="icon" onClick={comms.openEmailDialog} data-testid="email-btn-sidebar" className={`h-7 w-7 ${(lead.dne || !canCommunicate) ? 'bg-slate-300' : 'bg-indigo-600 hover:bg-indigo-700'}`} title={!canCommunicate ? "Only the lead owner can email" : "Email"} disabled={lead.dne || !canCommunicate}>
                      <Mail className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={comms.openSmsDialog} data-testid="sms-btn-sidebar" title={!canCommunicate ? "Only the lead owner can SMS" : "SMS"} className="h-7 w-7" disabled={!canCommunicate}><MessageSquare className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="icon" onClick={comms.openLogCallDialog} data-testid="call-btn-sidebar" title={!canCommunicate ? "Only the lead owner can call" : "Call"} className="h-7 w-7" disabled={lead.dnc || !canCommunicate}><PhoneCall className="h-3.5 w-3.5" /></Button>
                    <Button variant="outline" size="icon" onClick={() => tasksHook.setTaskDialogOpen(true)} data-testid="task-btn-sidebar" title={!canCommunicate ? "Only the lead owner can add tasks" : "Task"} className="h-7 w-7" disabled={!canCommunicate}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>

                {/* Company, Title, Status */}
                {lead.company_name && (
                  lead.company_id
                    ? <button onClick={() => navigate(`/admin/companies/${lead.company_id}`)} className="text-indigo-600 font-medium text-xs mb-0.5 hover:underline hover:text-indigo-800 text-left">{lead.company_name}</button>
                    : <p className="text-indigo-600 font-medium text-xs mb-0.5">{lead.company_name}</p>
                )}
                {leadCompany?.website && (
                  <a href={leadCompany.website.startsWith('http') ? leadCompany.website : `https://${leadCompany.website}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-600 text-xs flex items-center gap-1 mb-0.5 truncate">
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{leadCompany.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
                <p className="text-slate-500 text-xs mb-1.5">{lead.title || 'No title'}</p>
                <div className="mb-2">{getStatusBadge(lead.status)}</div>
                
                {/* Lead Types - Wrap in 2 rows */}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-1.5">Lead Type</p>
                  <div className="flex flex-wrap gap-1">
                    {LEAD_TYPES.map(type => {
                      const isSelected = (lead.lead_types || []).includes(type.value);
                      return (
                        <button
                          key={type.value}
                          onClick={async () => {
                            const currentTypes = lead.lead_types || [];
                            const newTypes = isSelected 
                              ? currentTypes.filter(t => t !== type.value)
                              : [...currentTypes, type.value];
                            try {
                              await axios.put(`${API}/leads/${lead.id}`, { lead_types: newTypes });
                              setLead({ ...lead, lead_types: newTypes });
                              toast.success(`Lead type ${isSelected ? 'removed' : 'added'}`);
                            } catch (error) {
                              toast.error('Failed to update lead type');
                            }
                          }}
                          className={`px-1.5 py-0.5 rounded-full text-xs font-medium transition-all ${
                            isSelected 
                              ? type.color + ' ring-1 ring-offset-1 ring-current' 
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                          title={isSelected ? `Remove ${type.label}` : `Add ${type.label}`}
                        >
                          {type.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Owner Section */}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-1.5">Owner</p>
                  {lead.owner_id === currentUser?.id ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Mine</span>
                  ) : lead.owner_id ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{lead.owner_name || 'Assigned'}</span>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Unassigned</span>
                  )}
                </div>

                {/* Lists Section - Vertical */}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-1.5">Lists</p>
                  {leadLists && leadLists.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {leadLists.map(list => (
                        <Badge 
                          key={list.id} 
                          variant="outline" 
                          className="px-1.5 py-0.5 text-xs"
                          style={{ backgroundColor: `${list.color}20`, borderColor: list.color, color: list.color }}
                        >
                          {list.name}
                          <button
                            onClick={async () => {
                              if (!canCommunicate) return;
                              try {
                                await axios.post(`${API}/leads/${lead.id}/remove-from-list/${list.id}`);
                                setLeadLists(leadLists.filter(l => l.id !== list.id));
                                toast.success(`Removed from ${list.name}`);
                              } catch (error) {
                                toast.error('Failed to remove from list');
                              }
                            }}
                            className={`ml-1 rounded-full p-0.5 ${canCommunicate ? 'hover:bg-white/50' : 'opacity-30 cursor-not-allowed'}`}
                            title={canCommunicate ? "Remove from list" : "Only the lead owner can modify lists"}
                            disabled={!canCommunicate}
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic mb-1.5">Not in any lists</p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddToListDialogOpen(true)}
                    className="w-full text-xs h-6"
                    disabled={!canCommunicate}
                    title={!canCommunicate ? "Only the lead owner can modify lists" : "Add to List"}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add to List
                  </Button>
                </div>

                {/* Sequences Section - Vertical */}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-1.5">Sequences</p>
                  {leadEnrollments && leadEnrollments.length > 0 ? (
                    <div className="space-y-1.5 mb-1.5">
                      {leadEnrollments.filter(e => e.status === 'active').map(enrollment => (
                        <div key={enrollment.id} className="p-1.5 bg-blue-50 border border-blue-200 rounded">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-semibold text-blue-900 truncate">
                              {enrollment.sequence_name || 'Sequence'}
                            </span>
                            <button
                              onClick={(e) => canCommunicate && handleRemoveFromSequence(enrollment.sequence_id, enrollment.id, e)}
                              className={`flex-shrink-0 ${canCommunicate ? 'text-red-600 hover:text-red-800' : 'text-slate-300 cursor-not-allowed'}`}
                              title={canCommunicate ? "Remove from sequence" : "Only the lead owner can modify sequences"}
                              disabled={!canCommunicate}
                              type="button"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="text-xs text-blue-700">
                            Step {(enrollment.steps_completed || 0) + 1} of {enrollment.total_steps || '?'}
                          </p>
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs mt-0.5">
                            Active
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic mb-1.5">Not in any sequences</p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEnrollInSequenceDialogOpen(true)}
                    className="w-full text-xs h-6"
                    disabled={!canCommunicate}
                    title={!canCommunicate ? "Only the lead owner can manage sequences" : "Enroll in Sequence"}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Enroll in Sequence
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Contact Information</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {lead._contact_masked && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Lock className="h-4 w-4 flex-shrink-0" />
                  <span>Contact details are visible only to this lead's owner.</span>
                </div>
              )}
              {lead.work_email && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-indigo-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Work Email</p>
                    <a href={'mailto:' + lead.work_email} className="text-sm text-indigo-600 hover:underline">{lead.work_email}</a>
                  </div>
                </div>
              )}
              {lead.personal_email && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><Mail className="h-4 w-4 text-slate-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">Personal Email</p>
                    <a href={'mailto:' + lead.personal_email} className="text-sm text-slate-700">{lead.personal_email}</a>
                  </div>
                </div>
              )}
              {(lead.mobile_phone || lead.work_direct_phone || lead.work_company_phone) && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><Phone className="h-4 w-4 text-green-600" /></div>
                  <div className="space-y-1">
                    {lead.work_direct_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Work Direct</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.work_direct_phone)} 
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.work_direct_phone)} disabled={telnyx.callingNumber === lead.work_direct_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.work_direct_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.work_direct_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.work_direct_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"} data-testid="work-direct-sms-btn">
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {lead.mobile_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Mobile</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.mobile_phone)}
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.mobile_phone)} disabled={telnyx.callingNumber === lead.mobile_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.mobile_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.mobile_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.mobile_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"} data-testid="mobile-sms-btn">
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    {lead.work_company_phone && (
                      <div>
                        <p className="text-xs text-slate-500">Company</p>
                        {lead.dnc || lead.bad_number ? (
                          <span className="text-sm text-slate-400 flex items-center gap-1">
                            {formatPhone(lead.work_company_phone)}
                            {lead.bad_number && <XCircle className="h-3 w-3 text-red-500" title="Bad Number" />}
                            {lead.dnc && <Ban className="h-3 w-3 text-red-400" title="Do Not Call" />}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => telnyx.handleClickToCall(lead.work_company_phone)} disabled={telnyx.callingNumber === lead.work_company_phone || !canCommunicate} className={`text-sm flex items-center gap-1 ${canCommunicate ? 'text-slate-700 hover:text-green-600 hover:underline' : 'text-slate-400 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can call" : telnyx.telnyxStatus.configured ? "Click to call via Telnyx" : "Click to dial"}>
                              {telnyx.callingNumber === lead.work_company_phone ? (<><Loader2 className="h-3 w-3 animate-spin" /> Calling...</>) : (<>{formatPhone(lead.work_company_phone)} <PhoneCall className="h-3 w-3" /></>)}
                            </button>
                            <button onClick={() => { comms.setSmsForm(f => ({ ...f, to: lead.work_company_phone })); comms.setSmsDialogOpen(true); }} disabled={!canCommunicate} className={`p-1 rounded ${canCommunicate ? 'hover:bg-green-100 text-green-600 hover:text-green-700' : 'text-slate-300 cursor-not-allowed'}`} title={!canCommunicate ? "Only the lead owner can SMS" : "Send SMS"}>
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {lead.linkedin_url && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0"><Linkedin className="h-4 w-4 text-blue-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">LinkedIn</p>
                    <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {lead.x_twitter_url && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold">𝕏</span></div>
                  <div>
                    <p className="text-xs text-slate-500">X/Twitter</p>
                    <a href={lead.x_twitter_url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {lead.linkedin_url_x && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0"><Linkedin className="h-4 w-4 text-indigo-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">LinkedIn URL-X</p>
                    <a href={lead.linkedin_url_x} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline">View Profile</a>
                  </div>
                </div>
              )}
              {leadCompany?.ats_used && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0"><Briefcase className="h-4 w-4 text-orange-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">ATS Used</p>
                    <p className="text-sm text-slate-700">{leadCompany.ats_used}</p>
                  </div>
                </div>
              )}
              {leadCompany?.ai_voice_using && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0"><Cpu className="h-4 w-4 text-violet-600" /></div>
                  <div>
                    <p className="text-xs text-slate-500">AI Voice</p>
                    <p className="text-sm text-slate-700">{leadCompany.ai_voice_using}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Card - 2x2 Grid */}
          <Card>
            <CardHeader className="pb-2 pt-3 px-3"><CardTitle className="text-xs font-semibold">Quick Actions</CardTitle></CardHeader>
            <CardContent className="px-3 pb-3">
              {/* 2x2 Grid of Checkboxes */}
              <div className="grid grid-cols-2 gap-1.5">
                <div className="flex items-center justify-between p-1.5 rounded border hover:bg-slate-50">
                  <div className="flex items-center gap-1">
                    <Ban className={`h-3 w-3 ${lead.dnc ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className="text-xs">DNC</span>
                  </div>
                  <Checkbox checked={lead.dnc || false} disabled={!canCommunicate} onCheckedChange={(checked) => canCommunicate && profile.handleToggleDncDne('dnc', checked, setLead)} className="h-3.5 w-3.5" />
                </div>
                <div className="flex items-center justify-between p-1.5 rounded border hover:bg-slate-50">
                  <div className="flex items-center gap-1">
                    <MailCheck className={`h-3 w-3 ${lead.dne ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className="text-xs">DNE</span>
                  </div>
                  <Checkbox checked={lead.dne || false} disabled={!canCommunicate} onCheckedChange={(checked) => canCommunicate && profile.handleToggleDncDne('dne', checked, setLead)} className="h-3.5 w-3.5" />
                </div>
                <div className="flex items-center justify-between p-1.5 rounded border hover:bg-slate-50">
                  <div className="flex items-center gap-1">
                    <XCircle className={`h-3 w-3 ${lead.bad_number ? 'text-red-500' : 'text-slate-400'}`} />
                    <span className="text-xs">Bad #</span>
                  </div>
                  <Checkbox checked={lead.bad_number || false} disabled={!canCommunicate} onCheckedChange={(checked) => canCommunicate && profile.handleToggleDncDne('bad_number', checked, setLead)} className="h-3.5 w-3.5" />
                </div>
                <div className="flex items-center justify-between p-1.5 rounded border hover:bg-slate-50">
                  <div className="flex items-center gap-1">
                    <PackageCheck className={`h-3 w-3 ${lead.envelope_mailed ? 'text-green-500' : 'text-slate-400'}`} />
                    <span className="text-xs">Env Mailed</span>
                  </div>
                  <Checkbox checked={lead.envelope_mailed || false} disabled={!canCommunicate} onCheckedChange={(checked) => canCommunicate && handleToggleEnvelopeMailed(checked)} className="h-3.5 w-3.5" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Colleagues Card */}
          {companyColleagues && companyColleagues.length > 0 && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-xs font-semibold flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5" />
                  Other Leads from {leadCompany?.name || 'Company'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 space-y-1">
                {companyColleagues.slice(0, 8).map((colleague) => (
                  <button
                    key={colleague.id}
                    onClick={() => navigate(`/admin/leads/${colleague.id}`)}
                    className="w-full text-left p-2 rounded hover:bg-indigo-50 transition-colors group"
                    data-testid={`colleague-${colleague.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <User className="h-3.5 w-3.5 text-slate-400 mt-0.5 group-hover:text-indigo-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700 group-hover:text-indigo-700 truncate">
                          {colleague.first_name} {colleague.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {colleague.title || 'No title'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
                {companyColleagues.length > 8 && (
                  <p className="text-xs text-slate-400 text-center pt-1">
                    +{companyColleagues.length - 8} more
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Content */}
        <div>
          {/* Contact Strategy Card */}
          <ContactStrategyCard lead={lead} leadCompany={leadCompany} />

          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start border-b rounded-none px-4">
                {tabOrder.map(tabKey => {
                  const tabLabels = {
                    notes: `Notes (${notes.length})`,
                    activity: `Activity (${communications.length})`,
                    tasks: `Tasks (${pendingTasks.length})`,
                    history: 'History',
                  };
                  if (!tabLabels[tabKey]) return null;
                  return (
                    <TabsTrigger
                      key={tabKey}
                      value={tabKey}
                      draggable
                      onDragStart={(e) => { setDragTabId(tabKey); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragOver={(e) => { e.preventDefault(); setDragOverTabId(tabKey); }}
                      onDragLeave={() => setDragOverTabId(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragTabId && dragTabId !== tabKey) {
                          setTabOrder(prev => {
                            const next = [...prev];
                            const fromIdx = next.indexOf(dragTabId);
                            const toIdx = next.indexOf(tabKey);
                            next.splice(fromIdx, 1);
                            next.splice(toIdx, 0, dragTabId);
                            localStorage.setItem('jtcrm_lead_tab_order', JSON.stringify(next));
                            return next;
                          });
                        }
                        setDragTabId(null);
                        setDragOverTabId(null);
                      }}
                      onDragEnd={() => { setDragTabId(null); setDragOverTabId(null); }}
                      className={`cursor-grab active:cursor-grabbing ${dragOverTabId === tabKey && dragTabId !== tabKey ? 'ring-2 ring-indigo-400 ring-offset-1' : ''} ${dragTabId === tabKey ? 'opacity-40' : ''}`}
                    >
                      {tabLabels[tabKey]}
                    </TabsTrigger>
                  );
                })}
                {(research || researching) && <TabsTrigger value="research" className="text-purple-600">{researching ? 'Researching...' : 'Research'}</TabsTrigger>}
                {leadCompany && <TabsTrigger value="company" className="text-blue-600">Company</TabsTrigger>}
                {leadCompany && <TabsTrigger value="dm" className="text-emerald-600">DM</TabsTrigger>}
              </TabsList>

              {/* Activity Tab */}
              <TabsContent value="activity" className="p-4 space-y-4 m-0">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {['all', 'email', 'sms', 'call', 'call_recording'].map(filter => (
                      <Button key={filter} variant={activityFilter === filter ? 'default' : 'outline'} size="sm" onClick={() => setActivityFilter(filter)} data-testid={`filter-${filter}`}>
                        {filter === 'all' ? 'All' : filter === 'call_recording' ? `Recordings (${communications.filter(c => c.type === 'call_recording' || c.has_recording).length})` : `${filter.charAt(0).toUpperCase() + filter.slice(1)} (${communications.filter(c => c.type === filter).length})`}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {communications.some(c => c.type === 'email' && c.synced_from_o365) && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleCleanEmails}
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        data-testid="clean-emails-btn"
                        title="Clean HTML artifacts from synced emails"
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Clean
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSyncEmailsDialogOpen(true)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="sync-emails-btn"
                      disabled={!lead.work_email && !lead.personal_email}
                      title={!lead.work_email && !lead.personal_email ? "No email address for this lead" : "Sync old emails from Office 365"}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Sync Old Emails
                    </Button>
                  </div>
                </div>
                {filteredCommunications.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No activity yet. Start by sending an email or making a call.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCommunications.map(comm => (
                      <div 
                        key={comm.id} 
                        className={`flex gap-3 p-3 border rounded-lg transition-colors group ${comm.type === 'email' ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/50' : comm.type === 'sms' ? 'hover:border-green-300 hover:bg-green-50/50' : comm.type === 'call' ? 'hover:border-amber-300 hover:bg-amber-50/50' : 'hover:border-indigo-200'}`}
                        onClick={() => comm.type === 'email' && handleEmailClick(comm, 'reply')}
                        data-testid={`activity-${comm.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${comm.type === 'email' ? 'bg-blue-100' : comm.type === 'sms' ? 'bg-green-100' : comm.type === 'call_recording' ? 'bg-purple-100' : 'bg-amber-100'}`}>
                          {comm.type === 'email' && <Mail className="h-5 w-5 text-blue-600" />}
                          {comm.type === 'sms' && <MessageSquare className="h-5 w-5 text-green-600" />}
                          {comm.type === 'call' && <Phone className="h-5 w-5 text-amber-600" />}
                          {comm.type === 'call_recording' && <Mic className="h-5 w-5 text-purple-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{comm.type === 'email' ? comm.subject : comm.type === 'sms' ? `SMS to ${formatPhone(comm.to)}` : comm.type === 'call_recording' ? 'Recording' : `Call to ${formatPhone(comm.to || comm.phone_number)}`}</span>
                            {comm.call_outcome && <Badge variant="outline" className="text-xs">{comm.call_outcome}</Badge>}
                            {comm.has_recording && <Badge className="bg-purple-100 text-purple-700 text-xs">Has Recording</Badge>}
                            {comm.type === 'call_recording' && <Badge className="bg-purple-100 text-purple-700 text-xs">Recording</Badge>}
                            {comm.type === 'email' && comm.direction === 'inbound' && <Badge className="bg-blue-100 text-blue-700 text-xs">Received</Badge>}
                            {comm.type === 'email' && comm.direction !== 'inbound' && comm.email_sent === true && <Badge className="bg-green-100 text-green-700 text-xs">Sent</Badge>}
                            {comm.type === 'email' && comm.direction !== 'inbound' && comm.email_sent === false && <Badge className="bg-amber-100 text-amber-700 text-xs">Logged Only</Badge>}
                            {comm.synced_from_o365 && <Badge className="bg-indigo-100 text-indigo-700 text-xs">O365</Badge>}
                            <EmailTrackingBadges comm={comm} />
                            {comm.sms_sent === true && <Badge className="bg-green-100 text-green-700 text-xs">Sent</Badge>}
                            {comm.sms_sent === false && comm.type === 'sms' && <Badge className="bg-amber-100 text-amber-700 text-xs">Logged Only</Badge>}
                          </div>
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{comm.type === 'email' ? cleanEmailContent(comm.content) : comm.content}</p>
                          {(comm.has_recording || comm.type === 'call_recording') && comm.recording_id && (
                            <div className="mt-2 p-2 bg-slate-50 rounded-lg" onClick={(e) => e.stopPropagation()}>
                              <audio controls className="w-full h-8" src={`${API}/telnyx/recordings/play/${comm.recording_id}`}>Your browser does not support the audio element.</audio>
                              {comm.duration_secs && <p className="text-xs text-slate-500 mt-1">Duration: {Math.floor(comm.duration_secs / 60)}:{(comm.duration_secs % 60).toString().padStart(2, '0')}</p>}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-slate-500 flex items-center gap-2">
                              <User className="h-3 w-3" />
                              {comm.user_name} • {comm.created_at ? format(parseISO(comm.created_at), 'MMM d, yyyy h:mm a') : ''}
                              {comm.direction === 'inbound' && comm.from_email && <span className="text-blue-600">• From: {comm.from_email}</span>}
                              {comm.direction === 'outbound' && comm.to && <span className="text-green-600">• To: {comm.to}</span>}
                            </p>
                            
                            {/* Hover action icons for emails */}
                            {comm.type === 'email' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-100 transition-colors"
                                  onClick={() => handleEmailClick(comm, 'reply')}
                                  data-testid={`reply-${comm.id}`}
                                  title="Reply"
                                >
                                  <Reply className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-1.5 rounded-md text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 transition-colors"
                                  onClick={() => handleEmailClick(comm, 'forward')}
                                  data-testid={`forward-${comm.id}`}
                                  title="Forward"
                                >
                                  <Forward className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            
                            {/* Hover action icons for SMS */}
                            {comm.type === 'sms' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-green-600 hover:text-green-700 hover:bg-green-100 transition-colors"
                                  onClick={() => handleSmsReplyClick(comm)}
                                  data-testid={`sms-reply-${comm.id}`}
                                  title="Reply to SMS"
                                >
                                  <Reply className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            
                            {/* Hover action icons for calls */}
                            {comm.type === 'call' && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  className="p-1.5 rounded-md text-amber-600 hover:text-amber-700 hover:bg-amber-100 transition-colors"
                                  onClick={() => handleCallClick(comm)}
                                  data-testid={`call-${comm.id}`}
                                  title="Call this lead"
                                  disabled={lead.dnc}
                                >
                                  <PhoneCall className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-1.5 rounded-md text-slate-600 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                  onClick={() => handleLogCallClick(comm)}
                                  data-testid={`log-call-${comm.id}`}
                                  title="Log a call"
                                >
                                  <FileText className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          {comm.attachments && comm.attachments.length > 0 && (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Paperclip className="h-3 w-3 text-slate-400" />
                              {comm.attachments.map((att, i) => (<Badge key={i} variant="outline" className="text-xs bg-slate-50">{att}</Badge>))}
                            </div>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); comms.handleDeleteCommunication(comm.id); }} data-testid={`delete-comm-${comm.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tasks Tab */}
              <TabsContent value="tasks" className="p-4 space-y-4 m-0">
                <Button size="sm" onClick={() => tasksHook.setTaskDialogOpen(true)} data-testid="add-task-btn" disabled={!canCommunicate} title={!canCommunicate ? "Only the lead owner can add tasks" : "Add Task"}><Plus className="h-4 w-4 mr-1" /> Add Task</Button>
                {tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No tasks yet. Create your first task.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingTasks.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Pending ({pendingTasks.length})</h4>
                        {pendingTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2 hover:border-indigo-200 transition-colors" data-testid={`task-${task.id}`}>
                            <Checkbox checked={task.completed} disabled={!canCommunicate} onCheckedChange={() => canCommunicate && tasksHook.handleToggleTask(task.id, task.completed)} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium">{task.description}</p>
                                {task.source === 'auto_from_note' && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200" title="Auto-generated from a note by AI">
                                    Auto
                                  </span>
                                )}
                              </div>
                              {task.due_date && <p className="text-xs text-slate-500">Due: {format(parseISO(task.due_date), 'MMM d, yyyy h:mm a')}</p>}
                            </div>
                            {canCommunicate && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => tasksHook.handleDeleteTask(task.id)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {completedTasks.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-500 mb-2">Completed ({completedTasks.length})</h4>
                        {completedTasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2 bg-slate-50 opacity-70">
                            <Checkbox checked={task.completed} disabled={!canCommunicate} onCheckedChange={() => canCommunicate && tasksHook.handleToggleTask(task.id, task.completed)} />
                            <div className="flex-1">
                              <p className="text-sm font-medium line-through">{task.description}</p>
                            </div>
                            {canCommunicate && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => tasksHook.handleDeleteTask(task.id)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Notes Tab */}
              <TabsContent value="notes" className="p-4 space-y-4 m-0">
                <div className="flex gap-2">
                  <Textarea placeholder="Add a note..." value={notesHook.newNote} onChange={(e) => notesHook.setNewNote(e.target.value)} rows={3} className="flex-1" data-testid="note-input" />
                  <Button onClick={notesHook.handleAddNote} disabled={notesHook.addingNote || !notesHook.newNote.trim()} data-testid="add-note-btn">
                    {notesHook.addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                {notes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No notes yet. Add your first note above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => {
                      const isEditing = editingNoteId === note.id;
                      return (
                        <div key={note.id} className="p-3 border rounded-lg hover:border-indigo-200 transition-colors group" data-testid={`note-${note.id}`}>
                          {isEditing ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editNoteContent}
                                onChange={(e) => setEditNoteContent(e.target.value)}
                                rows={3}
                                autoFocus
                                data-testid={`edit-note-input-${note.id}`}
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={savingEditNote}
                                  onClick={() => { setEditingNoteId(null); setEditNoteContent(''); }}
                                  data-testid={`cancel-edit-note-${note.id}`}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={savingEditNote || !editNoteContent.trim()}
                                  onClick={async () => {
                                    setSavingEditNote(true);
                                    try {
                                      const ok = await notesHook.handleEditNote(note.id, editNoteContent);
                                      if (ok !== false) {
                                        setEditingNoteId(null);
                                        setEditNoteContent('');
                                      }
                                    } finally {
                                      setSavingEditNote(false);
                                    }
                                  }}
                                  data-testid={`save-edit-note-${note.id}`}
                                >
                                  {savingEditNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-slate-500">
                                  {note.created_by_name} • {note.created_at ? formatDistanceToNow(parseISO(note.created_at), { addSuffix: true }) : ''}
                                  {note.updated_at && <span className="italic ml-1">(edited)</span>}
                                </p>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-500 hover:text-indigo-600"
                                    onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }}
                                    data-testid={`edit-note-${note.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600"
                                    onClick={() => notesHook.handleDeleteNote(note.id)}
                                    data-testid={`delete-note-${note.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="p-4 m-0">
                {auditLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No change history available.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {auditLogs.slice(0, 20).map((log, i) => (
                      <div key={i} className="flex items-start gap-3 p-2 text-sm border-b last:border-0">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full mt-1.5" />
                        <div className="flex-1">
                          <p className="text-slate-700">{log.action}</p>
                          <p className="text-xs text-slate-500">{log.user_name} • {log.created_at ? format(parseISO(log.created_at), 'MMM d, yyyy h:mm a') : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Research Tab — Dashboard Layout */}
              {(research || researching) && (
                <TabsContent value="research" className="p-4 m-0">
                  {researching && research?.status === 'in_progress' ? (
                    <div className="text-center py-12">
                      <style>{`
                        @keyframes fadeSlideIn {
                          0% { opacity: 0; transform: translateY(10px); }
                          100% { opacity: 1; transform: translateY(0); }
                        }
                        @keyframes pulseGlow {
                          0%, 100% { box-shadow: 0 0 8px rgba(147, 51, 234, 0.3); }
                          50% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.6); }
                        }
                      `}</style>
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4" style={{ animation: 'pulseGlow 2s ease-in-out infinite' }}>
                        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                      </div>
                      <p className="text-slate-700 font-semibold text-lg mb-1">Researching {lead.first_name} {lead.last_name}</p>

                      {/* Elapsed timer */}
                      <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1 mb-4">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className="text-xs font-mono text-slate-500">
                          {Math.floor(researchElapsed / 60)}:{String(researchElapsed % 60).padStart(2, '0')}
                        </span>
                      </div>

                      {/* Current step indicator */}
                      {researchProgress.step && (
                        <div className="mb-3 max-w-md mx-auto">
                          <div className="flex items-center justify-center gap-2 mb-1.5">
                            {researchProgress.step === 'starting' && <Cpu className="h-4 w-4 text-purple-400 animate-pulse" />}
                            {researchProgress.step === 'searching' && <Search className="h-4 w-4 text-blue-500 animate-pulse" />}
                            {researchProgress.step === 'scraping' && <Eye className="h-4 w-4 text-emerald-500 animate-pulse" />}
                            {researchProgress.step === 'analyzing' && <Sparkles className="h-4 w-4 text-amber-500 animate-pulse" />}
                            <span className="text-sm font-medium text-slate-600 capitalize">{researchProgress.step}</span>
                          </div>
                          {researchProgress.detail && (
                            <p key={researchProgress.detail} className="text-xs text-slate-400 truncate" style={{ animation: 'fadeSlideIn 0.4s ease-out' }}>
                              {researchProgress.detail}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Progress steps bar */}
                      <div className="flex items-center gap-1 justify-center mb-4">
                        {['starting', 'searching', 'scraping', 'analyzing'].map((s) => {
                          const steps = ['starting', 'searching', 'scraping', 'analyzing'];
                          const currentIdx = steps.indexOf(researchProgress.step);
                          const stepIdx = steps.indexOf(s);
                          const isDone = stepIdx < currentIdx;
                          const isCurrent = s === researchProgress.step;
                          return (
                            <div key={s} className="flex items-center gap-1">
                              <div className={`h-1.5 w-8 rounded-full transition-all duration-500 ${isDone ? 'bg-purple-500' : isCurrent ? 'bg-purple-400 animate-pulse' : 'bg-slate-200'}`} />
                            </div>
                          );
                        })}
                      </div>

                      {/* Funny message */}
                      <p key={researchMsgIndex} className="text-sm text-purple-500 font-medium" style={{ animation: 'fadeSlideIn 0.5s ease-out' }}>
                        {RESEARCH_MESSAGES[researchMsgIndex]}
                      </p>

                      <button onClick={handleCancelResearch} className="mt-5 text-xs text-slate-400 hover:text-red-500 transition-colors underline underline-offset-2">
                        Cancel research
                      </button>
                    </div>
                  ) : (research?.profile || research?.report) ? (() => {
                    const p = research.profile || {};
                    // Old markdown format — prompt to re-run for dashboard view
                    if (!research.profile && research.report) {
                      return (
                        <div className="text-center py-10 space-y-3">
                          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-purple-100 mb-2">
                            <RefreshCw className="h-6 w-6 text-purple-500" />
                          </div>
                          <p className="text-sm font-medium text-slate-700">This research uses an older format</p>
                          <p className="text-xs text-slate-400">Re-run to get the new dashboard view with DISC profile, sales intel, and more.</p>
                          <Button onClick={handleResearch} disabled={researching} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-4">
                            {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Re-run Research
                          </Button>
                          <details className="text-left mt-4 max-w-2xl mx-auto">
                            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">View old report</summary>
                            <div className="mt-2 prose prose-sm max-w-none text-slate-600 text-xs border rounded-lg p-3 max-h-60 overflow-y-auto">
                              <ReactMarkdown components={{ a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a> }}>
                                {research.report}
                              </ReactMarkdown>
                            </div>
                          </details>
                        </div>
                      );
                    }
                    const disc = p.personality || {};
                    const co = p.company || {};
                    const sa = p.sales_approach || {};
                    const discColors = { D: 'bg-red-500', I: 'bg-yellow-500', S: 'bg-green-500', C: 'bg-blue-500' };
                    const discBg = { D: 'bg-red-50 border-red-200', I: 'bg-yellow-50 border-yellow-200', S: 'bg-green-50 border-green-200', C: 'bg-blue-50 border-blue-200' };
                    const discText = { D: 'text-red-700', I: 'text-yellow-700', S: 'text-green-700', C: 'text-blue-700' };
                    // Truncate helper — first sentence or N chars
                    const short = (text, max = 60) => {
                      if (!text) return '';
                      const firstSentence = text.split(/[.!]\s/)[0];
                      if (firstSentence.length <= max) return firstSentence;
                      return text.slice(0, max).replace(/\s+\S*$/, '') + '...';
                    };
                    return (
                      <div className="space-y-3">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-400">{research.search_count || 0} sources &middot; {research.pages_scraped || 0} pages</span>
                            {(research.found_linkedin || lead.linkedin_url) && (
                              <a href={research.found_linkedin || lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#0A66C2] hover:text-[#004182]" title="LinkedIn">
                                <Linkedin className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{research.researched_at ? format(parseISO(research.researched_at), 'MMM d, yyyy') : ''}</span>
                            <Button variant="outline" size="sm" onClick={handleResearch} disabled={researching} className="text-purple-600 border-purple-200 hover:bg-purple-50 h-7 text-xs">
                              {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Refresh
                            </Button>
                          </div>
                        </div>

                        {/* === HERO CARD — Photo + DISC + Role + One-liner === */}
                        <div className="rounded-xl bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border border-purple-200 p-4">
                          <div className="flex items-center gap-4">
                            {(lead.photo_url || research.photo_url) && (
                              <div className="shrink-0">
                                <img src={lead.photo_url || research.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                            {disc.disc_type && (
                              <div className="shrink-0 text-center">
                                <div className={`w-14 h-14 rounded-full ${discColors[disc.disc_type] || 'bg-slate-400'} flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                                  {disc.disc_type}
                                </div>
                                <span className={`text-[10px] font-bold mt-0.5 block ${discText[disc.disc_type] || 'text-slate-700'}`}>{disc.disc_label || disc.disc_type}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {p.professional?.current_role && <p className="text-sm font-semibold text-slate-800 leading-tight">{p.professional.current_role}</p>}
                              {co.industry && <p className="text-[11px] text-slate-500 mt-0.5">{co.industry}{co.size ? ` · ${co.size}` : ''}{co.headquarters ? ` · ${co.headquarters}` : ''}</p>}
                              {(disc.traits || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {disc.traits.slice(0, 4).map((t, i) => (
                                    <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full ${discBg[disc.disc_type] || 'bg-slate-100'} ${discText[disc.disc_type] || 'text-slate-600'} font-medium border`}>{t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* === KPI CARDS — 4 columns, icon + label + short value === */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {sa.best_channel && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Phone className="h-3 w-3 text-emerald-500" /><span className="text-[10px] font-semibold text-emerald-700 uppercase">Channel</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(sa.best_channel, 50)}</p>
                            </div>
                          )}
                          {sa.timing && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Clock className="h-3 w-3 text-amber-500" /><span className="text-[10px] font-semibold text-amber-700 uppercase">Timing</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(sa.timing, 50)}</p>
                            </div>
                          )}
                          {disc.decision_style && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Cpu className="h-3 w-3 text-blue-500" /><span className="text-[10px] font-semibold text-blue-700 uppercase">Decisions</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{short(disc.decision_style, 50)}</p>
                            </div>
                          )}
                          {co.revenue && (
                            <div className="bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 mb-0.5"><Building2 className="h-3 w-3 text-violet-500" /><span className="text-[10px] font-semibold text-violet-700 uppercase">Revenue</span></div>
                              <p className="text-xs text-slate-700 leading-tight">{co.revenue}</p>
                            </div>
                          )}
                        </div>

                        {/* === RED FLAGS — only if present === */}
                        {(p.red_flags || []).length > 0 && (
                          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <Ban className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                            <div className="flex flex-wrap gap-1">
                              {p.red_flags.map((f, i) => (
                                <span key={i} className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{f}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === DO / DON'T — compact 2-col === */}
                        {((disc.communication_dos || []).length > 0 || (disc.communication_donts || []).length > 0) && (
                          <div className="grid grid-cols-2 gap-2">
                            {(disc.communication_dos || []).length > 0 && (
                              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                                <span className="text-[10px] font-bold text-green-700 uppercase">Do</span>
                                {disc.communication_dos.slice(0, 3).map((d, i) => (
                                  <div key={i} className="flex items-start gap-1 mt-1"><Check className="h-3 w-3 text-green-500 shrink-0 mt-0.5" /><span className="text-[11px] text-slate-700 leading-tight">{d}</span></div>
                                ))}
                              </div>
                            )}
                            {(disc.communication_donts || []).length > 0 && (
                              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <span className="text-[10px] font-bold text-red-700 uppercase">Don't</span>
                                {disc.communication_donts.slice(0, 3).map((d, i) => (
                                  <div key={i} className="flex items-start gap-1 mt-1"><X className="h-3 w-3 text-red-400 shrink-0 mt-0.5" /><span className="text-[11px] text-slate-700 leading-tight">{d}</span></div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* === OPENER — single best one === */}
                        {(sa.opening_lines || []).length > 0 && (
                          <div className="bg-white border border-purple-200 rounded-lg p-2.5 flex items-start gap-2.5 group">
                            <Sparkles className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <span className="text-[10px] font-bold text-purple-600 uppercase">Opener</span>
                              <p className="text-xs text-slate-700 italic mt-0.5">&ldquo;{sa.opening_lines[0]}&rdquo;</p>
                            </div>
                            <button onClick={() => { navigator.clipboard.writeText(sa.opening_lines[0]); toast.success('Copied!'); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-purple-50 rounded transition-opacity" title="Copy"><Copy className="h-3.5 w-3.5 text-purple-400" /></button>
                          </div>
                        )}

                        {/* === PAIN POINTS as pills === */}
                        {(sa.pain_points || []).length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase shrink-0 mt-1">Pain Points</span>
                            <div className="flex flex-wrap gap-1">
                              {sa.pain_points.map((pp, i) => (
                                <span key={i} className="text-[11px] bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full">{pp}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === EXPERTISE as pills === */}
                        {(p.professional?.expertise || []).length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-[10px] font-bold text-slate-500 uppercase shrink-0 mt-1">Expertise</span>
                            <div className="flex flex-wrap gap-1">
                              {p.professional.expertise.map((e, i) => (
                                <span key={i} className="text-[11px] bg-indigo-50 text-indigo-600 border border-indigo-200 px-2 py-0.5 rounded-full">{e}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* === FULL REPORT TOGGLE === */}
                        <button onClick={() => setShowFullReport(!showFullReport)} className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 py-2 border border-dashed border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showFullReport ? 'rotate-90' : ''}`} />
                          {showFullReport ? 'Hide full report' : 'Show full report'}
                        </button>

                        {showFullReport && (
                          <div className="space-y-3 pt-1">
                            {/* Summary */}
                            {p.summary && (
                              <div className="bg-slate-50 border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Summary</span>
                                <p className="text-xs text-slate-700 mt-1">{p.summary}</p>
                              </div>
                            )}

                            {/* Sales details */}
                            {(sa.value_prop || disc.email_style || disc.call_approach) && (
                              <div className="border rounded-lg p-3 space-y-2">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Sales Intel</span>
                                {sa.value_prop && <div className="mt-1"><span className="text-[10px] font-semibold text-slate-400">Value Prop</span><p className="text-xs text-slate-700">{sa.value_prop}</p></div>}
                                {disc.email_style && <div><span className="text-[10px] font-semibold text-slate-400">Email Style</span><p className="text-xs text-slate-700">{disc.email_style}</p></div>}
                                {disc.call_approach && <div><span className="text-[10px] font-semibold text-slate-400">Call Approach</span><p className="text-xs text-slate-700">{disc.call_approach}</p></div>}
                              </div>
                            )}

                            {/* More openers */}
                            {(sa.opening_lines || []).length > 1 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">More Openers</span>
                                <div className="mt-1 space-y-1.5">{sa.opening_lines.slice(1).map((line, i) => (
                                  <div key={i} className="flex items-start gap-2 group">
                                    <span className="text-[10px] text-slate-400 mt-0.5 shrink-0">{i+2}.</span>
                                    <p className="text-xs text-slate-700 flex-1 italic">&ldquo;{line}&rdquo;</p>
                                    <button onClick={() => { navigator.clipboard.writeText(line); toast.success('Copied!'); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded" title="Copy"><Copy className="h-3 w-3 text-slate-400" /></button>
                                  </div>
                                ))}</div>
                              </div>
                            )}

                            {/* Objections */}
                            {(sa.objections || []).length > 0 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Objection Handling</span>
                                <div className="mt-1 space-y-2">{sa.objections.map((obj, i) => (
                                  <div key={i}><p className="text-xs text-slate-700 font-medium">&ldquo;{obj.objection}&rdquo;</p><p className="text-[11px] text-slate-500 mt-0.5">{obj.response}</p></div>
                                ))}</div>
                              </div>
                            )}

                            {/* Company */}
                            {(co.description || co.hiring_activity) && (
                              <div className="border rounded-lg p-3 space-y-1.5">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Company{co.industry ? `: ${co.industry}` : ''}</span>
                                {co.description && <p className="text-xs text-slate-700">{co.description}</p>}
                                <div className="grid grid-cols-3 gap-2 text-[11px]">
                                  {co.size && <div><span className="text-slate-400 block">Size</span><span className="text-slate-700">{co.size}</span></div>}
                                  {co.revenue && <div><span className="text-slate-400 block">Revenue</span><span className="text-slate-700">{co.revenue}</span></div>}
                                  {co.headquarters && <div><span className="text-slate-400 block">HQ</span><span className="text-slate-700">{co.headquarters}</span></div>}
                                </div>
                                {co.hiring_activity && <div><span className="text-[10px] font-semibold text-slate-400">Hiring</span><p className="text-xs text-slate-600">{co.hiring_activity}</p></div>}
                                {(co.competitors || []).length > 0 && <p className="text-[11px] text-slate-500">Competitors: {co.competitors.join(', ')}</p>}
                              </div>
                            )}

                            {/* Career */}
                            {(p.professional?.career_history || p.professional?.education) && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Career</span>
                                {p.professional.career_history && <p className="text-xs text-slate-700 mt-1">{p.professional.career_history}</p>}
                                {p.professional.education && <p className="text-[11px] text-slate-500 mt-1">{p.professional.education}</p>}
                              </div>
                            )}

                            {/* Content / Speaking / News */}
                            {((p.content || []).length > 0 || (p.speaking || []).length > 0 || (p.news || []).length > 0) && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Content & Media</span>
                                <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                                  {(p.content || []).length > 0 && <div>{p.content.slice(0, 3).map((c, i) => <div key={i} className="mt-0.5">{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-600 hover:underline">{c.title}</a> : <span className="text-[11px] text-slate-700">{c.title}</span>}</div>)}</div>}
                                  {(p.speaking || []).length > 0 && <div>{p.speaking.slice(0, 3).map((s, i) => <div key={i} className="mt-0.5"><span className="text-[11px] text-slate-700">{s.event}</span></div>)}</div>}
                                  {(p.news || []).length > 0 && <div>{p.news.slice(0, 3).map((n, i) => <div key={i} className="mt-0.5">{n.url ? <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-indigo-600 hover:underline">{n.headline}</a> : <span className="text-[11px] text-slate-700">{n.headline}</span>}</div>)}</div>}
                                </div>
                              </div>
                            )}

                            {/* Key Connections */}
                            {(p.key_connections || []).length > 0 && (
                              <div className="border rounded-lg p-3">
                                <span className="text-[10px] font-bold text-slate-500 uppercase">Key Connections</span>
                                <div className="flex flex-wrap gap-1 mt-1">{p.key_connections.map((c, i) => <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c}</span>)}</div>
                              </div>
                            )}

                            {/* Sources */}
                            {(research.citations || []).length > 0 && (
                              <details className="text-xs">
                                <summary className="text-slate-400 cursor-pointer hover:text-slate-600">{research.citations.length} sources</summary>
                                <div className="mt-2 space-y-0.5 pl-2">
                                  {research.citations.map((c, i) => (
                                    <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="block text-indigo-600 hover:underline truncate text-[11px]">{c.title || c.url}</a>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })() : research ? (
                    <div className="text-center py-10 space-y-3">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-2">
                        <Search className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        {research.status === 'failed' ? 'Research failed' : 'No research data available'}
                      </p>
                      {research.error && <p className="text-xs text-red-500">{research.error}</p>}
                      <Button onClick={handleResearch} disabled={researching} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-4">
                        {researching ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Run Research
                      </Button>
                    </div>
                  ) : null}
                </TabsContent>
              )}

              {/* Company Research Tab */}
              {leadCompany && (
                <TabsContent value="company" className="p-4 m-0">
                  <CompanyResearchTab companyId={leadCompany.id} companyName={leadCompany.name} />
                </TabsContent>
              )}

              {/* Decision Makers Tab */}
              {leadCompany && (
                <TabsContent value="dm" className="p-4 m-0">
                  <CompanyDecisionMakersTab companyId={leadCompany.id} companyName={leadCompany.name} />
                </TabsContent>
              )}
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Email Dialog */}
      <DraggableDialog 
        open={comms.emailDialogOpen} 
        onClose={() => { comms.setEmailDialogOpen(false); setSelectedEmail(null); setEmailActionType(null); }} 
        title={emailActionType === 'reply' ? 'Reply to Email' : emailActionType === 'forward' ? 'Forward Email' : 'Send Email'} 
        maxWidth="max-w-2xl"
      >
        <div className="space-y-4">
          {/* Show original email info when replying/forwarding */}
          {selectedEmail && (
            <div className="bg-slate-50 border rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                <Mail className="h-4 w-4" />
                <span className="font-medium">{emailActionType === 'reply' ? 'Replying to:' : 'Forwarding:'}</span>
              </div>
              <p className="text-sm font-medium text-slate-800">{selectedEmail.subject}</p>
              <p className="text-xs text-slate-500 mt-1">
                {selectedEmail.direction === 'inbound' ? `From: ${selectedEmail.from_email}` : `To: ${selectedEmail.to}`}
                {' • '}
                {selectedEmail.created_at ? format(parseISO(selectedEmail.created_at), 'MMM d, yyyy h:mm a') : ''}
              </p>
            </div>
          )}
          
          {/* Template dropdown - always shown */}
          <div className="space-y-2">
            <Label>Email Template</Label>
            <TemplateCombobox templates={emailTemplates} value={comms.emailForm.template_id} onChange={comms.handleEmailTemplateChange} placeholder="Select a template..." testId="email-template-select" />
          </div>
          
          <div className="space-y-2">
            <Label>To *</Label>
            <Input value={comms.emailForm.to} onChange={(e) => comms.setEmailForm(f => ({ ...f, to: e.target.value }))} type="email" data-testid="email-to" placeholder={emailActionType === 'forward' ? 'Enter recipient email...' : ''} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CC</Label>
              <Input value={comms.emailForm.cc} onChange={(e) => comms.setEmailForm(f => ({ ...f, cc: e.target.value }))} placeholder="email1@example.com, email2@example.com" />
            </div>
            <div className="space-y-2">
              <Label>BCC</Label>
              <Input value={comms.emailForm.bcc} onChange={(e) => comms.setEmailForm(f => ({ ...f, bcc: e.target.value }))} placeholder="email@example.com" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Subject *</Label>
              <VariablesHelper onInsert={(v) => comms.insertVariable(v, 'email', 'subject')} />
            </div>
            <Input ref={comms.emailSubjectRef} value={comms.emailForm.subject} onChange={(e) => comms.setEmailForm(f => ({ ...f, subject: e.target.value }))} data-testid="email-subject" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label>Body *</Label>
                {/* AI buttons for email body - always visible */}
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => openAiPromptDialog('email')}
                    disabled={aiProcessing}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Generate AI email content"
                    data-testid="ai-write-btn"
                  >
                    {aiProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    Write with AI
                  </button>
                  <button
                    onClick={handleAiRewrite}
                    disabled={aiProcessing || !comms.emailForm.body.trim()}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Rewrite your draft to be more professional"
                    data-testid="ai-rewrite-btn"
                  >
                    {aiProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    Rewrite with AI
                  </button>
                </div>
              </div>
              <VariablesHelper onInsert={(v) => comms.insertVariable(v, 'email', 'body')} />
            </div>
            <RichTextEditor value={comms.emailForm.body} onChange={(html) => comms.setEmailForm(f => ({ ...f, body: html }))} placeholder="Write your email..." minHeight="180px" />
            {/* Signature toggle */}
            {comms.signatureHtml && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => comms.setIncludeSignature(v => !v)}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                    comms.includeSignature
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-50 text-slate-500 border border-slate-200'
                  }`}
                >
                  <Edit className="h-3 w-3" />
                  {comms.includeSignature ? 'Signature included' : 'Add signature'}
                </button>
                {comms.includeSignature && (
                  <div className="mt-2 border rounded-md p-3 bg-slate-50 text-sm">
                    <div dangerouslySetInnerHTML={{ __html: comms.signatureHtml }} />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Attachments</Label>
            <div className="flex items-center gap-2 flex-wrap">
              {comms.emailForm.attachments.map((file, index) => (
                <Badge key={index} variant="outline" className="gap-1 py-1.5">
                  <Paperclip className="h-3 w-3" />
                  {file.name}
                  <button onClick={() => comms.removeAttachment(index)} className="ml-1 text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              <Button variant="outline" size="sm" onClick={() => comms.setAttachLibraryOpen(true)}><FolderOpen className="h-4 w-4 mr-1" /> From Library</Button>
              <Button variant="outline" size="sm" onClick={() => comms.fileInputRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> From Computer</Button>
              <input ref={comms.fileInputRef} type="file" multiple onChange={comms.handleFileAttachment} className="hidden" />
            </div>
            <p className="text-xs text-slate-500">Attach documents from the library or upload from your computer.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => { comms.setEmailDialogOpen(false); setSelectedEmail(null); setEmailActionType(null); }}>Cancel</Button>
          <Button onClick={() => { comms.handleSendEmail(); setSelectedEmail(null); setEmailActionType(null); }} disabled={comms.saving || aiProcessing} data-testid="send-email-btn">
            {comms.saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Send className="h-4 w-4 mr-2" /> 
            {emailActionType === 'reply' ? 'Send Reply' : emailActionType === 'forward' ? 'Forward' : 'Send Email'}
          </Button>
        </div>
      </DraggableDialog>

      {/* Attach from Library Modal */}
      <AttachFromLibraryModal open={comms.attachLibraryOpen} onOpenChange={comms.setAttachLibraryOpen} defaultTeamFilter="all" alreadyAttached={comms.emailForm.attachments.filter(a => a.source === 'library').map(a => a.id)} onAttach={comms.addLibraryAttachments} />

      {/* SMS Dialog */}
      <DraggableDialog open={comms.smsDialogOpen} onClose={() => comms.setSmsDialogOpen(false)} title="Send SMS" maxWidth="max-w-lg">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>SMS Template</Label>
            <TemplateCombobox templates={smsTemplates} value={comms.smsForm.template_id} onChange={comms.handleSmsTemplateChange} placeholder="Select a template..." testId="sms-template-select" />
          </div>
          <div className="space-y-2">
            <Label>To *</Label>
            {/* Show dropdown if lead has multiple phone numbers */}
            {(() => {
              const phoneNumbers = [];
              if (lead?.mobile_phone) phoneNumbers.push({ label: 'Mobile', value: lead.mobile_phone });
              if (lead?.work_direct_phone) phoneNumbers.push({ label: 'Work Direct', value: lead.work_direct_phone });
              if (lead?.work_hq_phone) phoneNumbers.push({ label: 'Work HQ', value: lead.work_hq_phone });
              
              if (phoneNumbers.length > 1) {
                return (
                  <Select
                    value={comms.smsForm.to}
                    onValueChange={(value) => comms.setSmsForm(f => ({ ...f, to: value }))}
                  >
                    <SelectTrigger data-testid="sms-to-select">
                      <SelectValue placeholder="Select phone number" />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneNumbers.map((phone, idx) => (
                        <SelectItem key={idx} value={phone.value}>
                          <div className="flex items-center gap-2">
                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-xs text-slate-500 w-20">{phone.label}:</span>
                            <span className="font-mono">{phone.value}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }
              return <Input value={comms.smsForm.to} onChange={(e) => comms.setSmsForm(f => ({ ...f, to: e.target.value }))} data-testid="sms-to" />;
            })()}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label>Message *</Label>
                {/* AI buttons for SMS */}
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => openAiPromptDialog('sms')}
                    disabled={aiProcessing}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Generate AI SMS"
                    data-testid="ai-sms-write-btn"
                  >
                    {aiProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                    Write with AI
                  </button>
                  <button
                    onClick={handleAiSmsRewrite}
                    disabled={aiProcessing || !comms.smsForm.message.trim()}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Rewrite your SMS to be more professional"
                    data-testid="ai-sms-rewrite-btn"
                  >
                    {aiProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    Rewrite with AI
                  </button>
                </div>
              </div>
              <VariablesHelper onInsert={(v) => comms.insertVariable(v, 'sms', 'message')} />
            </div>
            <Textarea ref={comms.smsMessageRef} value={comms.smsForm.message} onChange={(e) => comms.setSmsForm(f => ({ ...f, message: e.target.value }))} rows={4} data-testid="sms-message" />
            <p className="text-xs text-slate-500">{comms.smsForm.message.length} characters</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => comms.setSmsDialogOpen(false)}>Cancel</Button>
          <Button onClick={comms.handleSendSMS} disabled={comms.saving || aiProcessing} data-testid="send-sms-btn">
            {comms.saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Send className="h-4 w-4 mr-2" /> Send SMS
          </Button>
        </div>
      </DraggableDialog>

      {/* AI Prompt Dialog */}
      <Dialog open={aiPromptDialogOpen} onOpenChange={setAiPromptDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              {aiPromptType === 'email' ? 'Generate Email with AI' : 'Generate SMS with AI'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ai-prompt">What would you like to write about?</Label>
              <Textarea
                id="ai-prompt"
                placeholder={aiPromptType === 'email' 
                  ? "e.g., Follow up on our meeting, Schedule a demo call, Introduce our staffing services..."
                  : "e.g., Quick follow-up, Meeting reminder, Availability check..."
                }
                value={aiPromptText}
                onChange={(e) => setAiPromptText(e.target.value)}
                rows={3}
                data-testid="ai-prompt-input"
                className="resize-none"
              />
              <p className="text-xs text-slate-500">
                {aiPromptType === 'email' 
                  ? "Describe the purpose of your email. AI will generate a professional message."
                  : "Describe what you want to say. AI will create a concise SMS message."
                }
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setAiPromptDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAiPromptSubmit} 
              disabled={aiProcessing}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="ai-generate-btn"
            >
              {aiProcessing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Generate</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Call Dialog - Using shared component */}
      <LogCallDialog
        open={comms.callDialogOpen}
        onClose={() => comms.setCallDialogOpen(false)}
        lead={lead}
        callForm={comms.callForm}
        setCallForm={comms.setCallForm}
        onSubmit={comms.handleLogCall}
        saving={comms.saving}
        templates={templates}
        leadId={id}
        onClickToCall={(phoneNumber) => telnyx.handleClickToCall(phoneNumber)}
      />

      {/* Task Dialog */}
      <Dialog open={tasksHook.taskDialogOpen} onOpenChange={tasksHook.setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Task Type</Label>
              <Select value={tasksHook.taskForm.task_type} onValueChange={(v) => tasksHook.setTaskForm(f => ({ ...f, task_type: v }))}>
                <SelectTrigger data-testid="task-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="task">Task</SelectItem>
                  <SelectItem value="call">📞 Call</SelectItem>
                  <SelectItem value="meeting">📅 Meeting</SelectItem>
                </SelectContent>
              </Select>
              {['call', 'meeting'].includes(tasksHook.taskForm.task_type) && (
                <p className="text-xs text-indigo-600 flex items-center gap-1">
                  <CalendarPlus className="h-3 w-3" />
                  A calendar event will be created with a 30-min reminder. You'll receive email reminders 1 day and 15 minutes before.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={tasksHook.taskForm.description} onChange={(e) => tasksHook.setTaskForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="What needs to be done?" data-testid="task-description" />
            </div>
            <div className="space-y-2">
              <Label>Scheduled Date & Time</Label>
              <Input type="datetime-local" value={tasksHook.taskForm.due_date} onChange={(e) => tasksHook.setTaskForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => tasksHook.setTaskDialogOpen(false)}>Cancel</Button>
            <Button onClick={tasksHook.handleCreateTask} disabled={tasksHook.saving} data-testid="create-task-btn">
              {tasksHook.saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={profile.editDialogOpen} onOpenChange={profile.setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Lead</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Prefix</Label><Select value={profile.formData.prefix || 'none'} onValueChange={(v) => profile.setFormData(f => ({ ...f, prefix: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{PREFIXES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>First Name *</Label><Input value={profile.formData.first_name || ''} onChange={(e) => profile.setFormData(f => ({ ...f, first_name: e.target.value }))} data-testid="edit-first-name" /></div>
              <div><Label>Last Name</Label><Input value={profile.formData.last_name || ''} onChange={(e) => profile.setFormData(f => ({ ...f, last_name: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Gender</Label><Select value={profile.formData.gender || 'none'} onValueChange={(v) => profile.setFormData(f => ({ ...f, gender: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{GENDERS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Title</Label><Input value={profile.formData.title || ''} onChange={(e) => profile.setFormData(f => ({ ...f, title: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <Label>Company</Label>
                <Input
                  value={editCompanySearchQuery}
                  onChange={(e) => {
                    setEditCompanySearchQuery(e.target.value);
                    setShowEditCompanySuggestions(true);
                    if (!e.target.value) {
                      profile.handleCompanyChange('');
                    }
                  }}
                  onFocus={() => setShowEditCompanySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowEditCompanySuggestions(false), 200)}
                  placeholder="Search or add company..."
                  autoComplete="off"
                />
                {showEditCompanySuggestions && editCompanySearchQuery && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {companies
                      .filter(c => c.name.toLowerCase().includes(editCompanySearchQuery.toLowerCase()))
                      .slice(0, 10)
                      .map((company) => (
                        <div
                          key={company.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm text-slate-900"
                          onClick={() => {
                            profile.handleCompanyChange(company.id);
                            setEditCompanySearchQuery(company.name);
                            setShowEditCompanySuggestions(false);
                          }}
                        >
                          {company.name}
                        </div>
                      ))}
                    {companies.filter(c => c.name.toLowerCase().includes(editCompanySearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-400 italic">No companies found</div>
                    )}
                    <div className="border-t px-3 py-2">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium w-full"
                        onClick={() => {
                          setNewCompanyForm({ name: editCompanySearchQuery.trim(), website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
                          setShowEditCompanySuggestions(false);
                          setNewCompanyDialogOpen(true);
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add New Company
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div><Label>Location</Label><Select value={profile.formData.location_id || 'none'} onValueChange={(v) => profile.setFormData(f => ({ ...f, location_id: v === 'none' ? '' : v }))} disabled={!profile.formData.company_id}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{profile.selectedCompanyLocations?.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Work Email</Label><Input type="email" value={profile.formData.work_email || ''} onChange={(e) => profile.setFormData(f => ({ ...f, work_email: e.target.value }))} /></div>
              <div><Label>Personal Email</Label><Input type="email" value={profile.formData.personal_email || ''} onChange={(e) => profile.setFormData(f => ({ ...f, personal_email: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Work Direct Phone</Label><Input value={profile.formData.work_direct_phone || ''} onChange={(e) => profile.setFormData(f => ({ ...f, work_direct_phone: e.target.value }))} data-testid="edit-work-phone" /></div>
              <div><Label>Work Company Phone</Label><Input value={profile.formData.work_company_phone || ''} onChange={(e) => profile.setFormData(f => ({ ...f, work_company_phone: e.target.value }))} data-testid="edit-company-phone" /></div>
              <div><Label>Mobile</Label><Input value={profile.formData.mobile_phone || ''} onChange={(e) => profile.setFormData(f => ({ ...f, mobile_phone: e.target.value }))} data-testid="edit-mobile-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>LinkedIn URL</Label><Input value={profile.formData.linkedin_url || ''} onChange={(e) => profile.setFormData(f => ({ ...f, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/in/..." data-testid="edit-linkedin" /></div>
              <div><Label>X/Twitter URL</Label><Input value={profile.formData.x_twitter_url || ''} onChange={(e) => profile.setFormData(f => ({ ...f, x_twitter_url: e.target.value }))} placeholder="https://x.com/..." /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>LinkedIn URL-X</Label><Input value={profile.formData.linkedin_url_x || ''} onChange={(e) => profile.setFormData(f => ({ ...f, linkedin_url_x: e.target.value }))} placeholder="https://linkedin.com/in/..." /></div>
              <div>
                <Label>Source</Label>
                <SourceSelect
                  value={profile.formData.source || ''}
                  onValueChange={(v) => profile.setFormData(f => ({ ...f, source: v }))}
                  existingSources={(allLeads || []).map(l => l.source).filter(Boolean)}
                />
              </div>
            </div>
            <div>
              <Label>Owner</Label>
              <Popover open={ownerSearchOpen} onOpenChange={(open) => { setOwnerSearchOpen(open); if (!open) setOwnerSearch(''); }}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {profile.formData.owner_name || (profile.formData.owner_id && allUsers.find(u => u.id === profile.formData.owner_id)?.full_name)
                      ? <span>{profile.formData.owner_name || allUsers.find(u => u.id === profile.formData.owner_id)?.full_name}</span>
                      : <span className="text-slate-400">Assign owner...</span>
                    }
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search or type a name..."
                      value={ownerSearch}
                      onValueChange={setOwnerSearch}
                    />
                    <CommandList className="max-h-52 overflow-y-auto">
                      <CommandGroup>
                        <CommandItem onSelect={() => {
                          profile.setFormData(f => ({ ...f, owner_id: null, owner_name: null }));
                          setOwnerSearch('');
                          setOwnerSearchOpen(false);
                        }}>
                          <Check className={`w-4 h-4 mr-2 ${!profile.formData.owner_id && !profile.formData.owner_name ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-slate-400">No Owner</span>
                        </CommandItem>
                        {allUsers
                          .filter(u => !ownerSearch.trim() || u.full_name?.toLowerCase().includes(ownerSearch.toLowerCase()))
                          .map(u => (
                            <CommandItem key={u.id} onSelect={() => {
                              profile.setFormData(f => ({ ...f, owner_id: u.id, owner_name: u.full_name }));
                              setOwnerSearch('');
                              setOwnerSearchOpen(false);
                            }}>
                              <Check className={`w-4 h-4 mr-2 ${profile.formData.owner_id === u.id ? 'opacity-100' : 'opacity-0'}`} />
                              {u.full_name}
                            </CommandItem>
                          ))
                        }
                        {ownerSearch.trim() && !allUsers.some(u => u.full_name?.toLowerCase() === ownerSearch.toLowerCase()) && (
                          <CommandItem onSelect={() => {
                            profile.setFormData(f => ({ ...f, owner_id: null, owner_name: ownerSearch.trim() }));
                            setOwnerSearch('');
                            setOwnerSearchOpen(false);
                          }} className="text-blue-600">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Use "{ownerSearch.trim()}" as custom owner
                          </CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div><Label>Status</Label><StatusSelect value={profile.formData.status || 'new'} onValueChange={(v) => profile.setFormData(f => ({ ...f, status: v }))} allStatuses={allStatuses} addStatus={addStatus} /></div>
            <div><Label>Description</Label><Textarea value={profile.formData.description || ''} onChange={(e) => profile.setFormData(f => ({ ...f, description: e.target.value }))} rows={3} /></div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Checkbox checked={profile.formData.dnc || false} onCheckedChange={(c) => profile.setFormData(f => ({ ...f, dnc: c }))} /><Label>Do Not Call</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={profile.formData.dne || false} onCheckedChange={(c) => profile.setFormData(f => ({ ...f, dne: c }))} /><Label>Do Not Email</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={profile.formData.envelope_mailed || false} onCheckedChange={(c) => profile.setFormData(f => ({ ...f, envelope_mailed: c }))} /><Label>Envelope Mailed</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => profile.setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={profile.handleUpdate} disabled={profile.saving} data-testid="update-lead-btn">
              {profile.saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={profile.deleteDialogOpen} onOpenChange={profile.setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Lead</AlertDialogTitle><AlertDialogDescription>This will delete all notes, tasks, and history. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={profile.handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* WebRTC Call Dialog */}
      <TelnyxWebRTCCall
        isOpen={telnyx.webrtcCallOpen}
        onClose={() => { telnyx.setWebrtcCallOpen(false); telnyx.setWebrtcCallNumber(null); }}
        phoneNumber={telnyx.webrtcCallNumber}
        leadId={id}
        leadName={lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : ''}
        companyName={(lead?.company_id && companies?.length > 0) ? (companies.find(c => c.id === lead.company_id)?.name || '') : ''}
        onCallConnected={telnyx.handleWebRTCCallConnected}
        onCallEnded={telnyx.handleWebRTCCallEnded}
        onDurationUpdate={telnyx.handleDurationUpdate}
        onError={(error) => telnyx.handleWebRTCError(error, telnyx.webrtcCallNumber)}
      />

      {/* Sync Old Emails Dialog */}
      <Dialog open={syncEmailsDialogOpen} onOpenChange={setSyncEmailsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Sync Old Emails from Office 365
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Sync historical emails from your Office 365 account for <strong>{lead?.work_email || lead?.personal_email}</strong>
            </p>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">How far back do you want to sync?</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={syncTimeValue}
                  onChange={(e) => setSyncTimeValue(e.target.value)}
                  className="w-24"
                  data-testid="sync-time-value"
                />
                <Select value={syncTimeUnit} onValueChange={setSyncTimeUnit}>
                  <SelectTrigger className="w-32" data-testid="sync-time-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="weeks">Weeks</SelectItem>
                    <SelectItem value="months">Months</SelectItem>
                    <SelectItem value="years">Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncEmailsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSyncOldEmails}
              disabled={syncingEmails}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-sync-emails-btn"
            >
              {syncingEmails ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Emails
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll in Sequence Dialog */}
      <Dialog open={enrollInSequenceDialogOpen} onOpenChange={setEnrollInSequenceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />
              Enroll in Sequence
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="select-sequence">Select Sequence *</Label>
              <Select value={selectedSequenceId} onValueChange={setSelectedSequenceId}>
                <SelectTrigger id="select-sequence">
                  <SelectValue placeholder="Choose a sequence..." />
                </SelectTrigger>
                <SelectContent>
                  {allSequences
                    .filter(seq => !leadEnrollments.find(e => e.sequence_id === seq.id && e.status === 'active'))
                    .map(seq => (
                      <SelectItem key={seq.id} value={seq.id}>
                        {seq.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {allSequences.filter(seq => !leadEnrollments.find(e => e.sequence_id === seq.id && e.status === 'active')).length === 0 && (
                <p className="text-sm text-slate-500">Lead is already in all active sequences</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollInSequenceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEnrollInSequence} disabled={enrollingInSequence || !selectedSequenceId}>
              {enrollingInSequence ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enrolling...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Enroll in Sequence
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to List Dialog */}
      <Dialog open={addToListDialogOpen} onOpenChange={setAddToListDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" />
              Add to List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="select-list">Select List *</Label>
              <Select value={selectedListId} onValueChange={setSelectedListId}>
                <SelectTrigger id="select-list">
                  <SelectValue placeholder="Choose a list..." />
                </SelectTrigger>
                <SelectContent>
                  {allLists
                    .filter(list => !leadLists.find(ll => ll.id === list.id))
                    .map(list => (
                      <SelectItem key={list.id} value={list.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: list.color }}
                          />
                          {list.name}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {allLists.filter(list => !leadLists.find(ll => ll.id === list.id)).length === 0 && (
                <p className="text-sm text-slate-500">Lead is already in all available lists</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddToListDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddToList} disabled={addingToList || !selectedListId}>
              {addingToList ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add to List
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Add New Company Dialog (from Edit Lead) */}
      <Dialog open={newCompanyDialogOpen} onOpenChange={setNewCompanyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-xs text-slate-500 -mt-2">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. All other fields are optional.</p>
            <div className="space-y-2">
              <Label>Company Name <span className="text-red-500">*</span></Label>
              <Input value={newCompanyForm.name} onChange={(e) => setNewCompanyForm({...newCompanyForm, name: e.target.value})} placeholder="e.g., Acme Corporation" />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={newCompanyForm.website} onChange={(e) => setNewCompanyForm({...newCompanyForm, website: e.target.value})} placeholder="e.g., https://acme.com" />
            </div>

            {/* Tech Stack */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input list="edit-ats-options" value={newCompanyForm.ats_used} onChange={(e) => setNewCompanyForm({...newCompanyForm, ats_used: e.target.value})} placeholder="e.g., JobDiva" />
                  <datalist id="edit-ats-options"><option value="JobDiva"/><option value="BullHorn"/><option value="Salesforce"/><option value="Greenhouse"/><option value="Workday"/><option value="iCIMS"/><option value="Taleo"/></datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input list="edit-ai-voice-options" value={newCompanyForm.ai_voice_using} onChange={(e) => setNewCompanyForm({...newCompanyForm, ai_voice_using: e.target.value})} placeholder="e.g., JobTalk" />
                  <datalist id="edit-ai-voice-options"><option value="JobTalk"/><option value="ConverzAI"/><option value="HeyMilo"/><option value="Glider"/><option value="Paradox"/><option value="HireVue"/></datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input value={newCompanyForm.other_ai_tools} onChange={(e) => setNewCompanyForm({...newCompanyForm, other_ai_tools: e.target.value})} placeholder="e.g., ChatGPT, Copilot" />
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label># of Recruiters</Label>
                  <Input type="number" min="0" value={newCompanyForm.num_recruiters} onChange={(e) => setNewCompanyForm({...newCompanyForm, num_recruiters: e.target.value})} placeholder="e.g., 25" />
                </div>
                <div className="space-y-2">
                  <Label>Number of Reqs Open</Label>
                  <Input type="number" min="0" value={newCompanyForm.num_reqs_open} onChange={(e) => setNewCompanyForm({...newCompanyForm, num_reqs_open: e.target.value})} placeholder="e.g., 100" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Geographies</Label>
                  <Input value={newCompanyForm.geographies} onChange={(e) => setNewCompanyForm({...newCompanyForm, geographies: e.target.value})} placeholder="e.g., USA, Canada, UK" />
                </div>
                <div className="space-y-2">
                  <Label>Years in Business</Label>
                  <Input type="number" min="0" value={newCompanyForm.years_in_business} onChange={(e) => setNewCompanyForm({...newCompanyForm, years_in_business: e.target.value})} placeholder="e.g., 15" />
                </div>
                <div className="space-y-2">
                  <Label>Revenues</Label>
                  <Input value={newCompanyForm.revenues} onChange={(e) => setNewCompanyForm({...newCompanyForm, revenues: e.target.value})} placeholder="e.g., $50M" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newCompanyForm.company_notes}
                  onChange={(e) => setNewCompanyForm({...newCompanyForm, company_notes: e.target.value})}
                  placeholder="Add notes about this company..."
                  className="min-h-[80px] resize-none text-sm"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCompanyDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCompanyFromEdit} disabled={savingCompany}>
              {savingCompany && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead from Same Company Dialog */}
      <Dialog open={addLeadDialogOpen} onOpenChange={setAddLeadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Add New Lead from {lead.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                <Building2 className="h-4 w-4 inline mr-1" />
                Company: <span className="font-semibold">{lead.company_name}</span>
                {lead.location_name && <> • Location: <span className="font-semibold">{lead.location_name}</span></>}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prefix</Label>
                <Select value={newLeadForm.prefix || 'none'} onValueChange={(v) => setNewLeadForm({...newLeadForm, prefix: v === 'none' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {PREFIXES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={newLeadForm.first_name} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, first_name: e.target.value})}
                  placeholder="John"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input 
                  value={newLeadForm.last_name} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, last_name: e.target.value})}
                  placeholder="Doe"
                />
              </div>
              <div>
                <Label>Title</Label>
                <Input 
                  value={newLeadForm.title} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, title: e.target.value})}
                  placeholder="VP of Sales"
                />
              </div>
              <div>
                <Label>Work Email</Label>
                <Input 
                  type="email"
                  value={newLeadForm.work_email} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, work_email: e.target.value})}
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <Label>Work Phone</Label>
                <Input 
                  value={newLeadForm.work_direct_phone} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, work_direct_phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <Label>Mobile Phone</Label>
                <Input 
                  value={newLeadForm.mobile_phone} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, mobile_phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input 
                  value={newLeadForm.linkedin_url} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, linkedin_url: e.target.value})}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div>
                <Label>X/Twitter URL</Label>
                <Input 
                  value={newLeadForm.x_twitter_url} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, x_twitter_url: e.target.value})}
                  placeholder="https://x.com/..."
                />
              </div>
              <div>
                <Label>LinkedIn URL-X</Label>
                <Input 
                  value={newLeadForm.linkedin_url_x} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, linkedin_url_x: e.target.value})}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLeadDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateNewLead} disabled={savingNewLead} className="bg-green-600 hover:bg-green-700">
              {savingNewLead ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Lead
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Event Dialog */}
      <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-blue-600" />
              Create Calendar Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Subject</label>
              <input
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={calendarForm.subject}
                onChange={(e) => setCalendarForm(f => ({ ...f, subject: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Start</label>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={calendarForm.start}
                  onChange={(e) => setCalendarForm(f => ({ ...f, start: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">End</label>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={calendarForm.end}
                  onChange={(e) => setCalendarForm(f => ({ ...f, end: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
              <textarea
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                value={calendarForm.notes}
                onChange={(e) => setCalendarForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalendarDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={calendarLoading || !calendarForm.start || !calendarForm.end}
              onClick={async () => {
                setCalendarLoading(true);
                try {
                  await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ai-calls/calendar-event`, {
                    lead_id: id,
                    subject: calendarForm.subject,
                    start_datetime: calendarForm.start.replace('T', 'T'),
                    end_datetime: calendarForm.end.replace('T', 'T'),
                    notes: calendarForm.notes
                  });
                  toast.success('Calendar event created with invite sent');
                  setCalendarDialogOpen(false);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to create calendar event');
                } finally {
                  setCalendarLoading(false);
                }
              }}
            >
              {calendarLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-1" />}
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
